import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../lib/auth.jsx';
import { LINE_CATEGORIES, cleanText } from '../lib/pi.js';
import { authFetch } from '../lib/authFetch.js';
import { preloadImages, afterPaint } from '../lib/print-images.js';
import html2canvas from 'html2canvas';
import ProductDrawer from '../components/ProductDrawer.jsx';
import ProductPicker from '../components/ProductPicker.jsx';
import { TZ, thDay, thToday } from '../lib/thTime.js';   // เวลาไทยเสมอ ไม่ตามนาฬิกาเครื่อง
import { useFxDefaults, fxDefaultOf } from '../lib/fxDefaults.js';

const CUR_SYM = { THB: '฿', JPY: '¥', USD: '$', EUR: '€', GBP: '£', CNY: '¥' };
const PO_CHIP = {
  draft: ['ร่าง', 'out'],
  ordering: ['กำลังสั่ง', 'out'],
  ordered: ['กำลังมา', 'amb'],
  arrived: ['ถึงแล้ว', 'amb'],
  completed: ['รับครบ', 'ok'],
  cancelled: ['ยกเลิก', 'out'],
};
const fmtDate = d => d
  ? new Date(d).toLocaleDateString('th-TH-u-ca-gregory', { timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric' })
  : '—';
const fmtBytes = n => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);
const fmtDT = d => (d ? new Date(d).toLocaleString('th-TH-u-ca-gregory', { timeZone: TZ, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d });
// รูปจาก PD ปกติเป็น URL เต็มอยู่แล้ว — เผื่อเจอ path สั้นจากที่อื่นก็เติม bucket ให้
const IMG = u => (!u ? '' : (/^https?:/.test(u) ? u : `https://nwfovqicnyxjobkbjcva.supabase.co/storage/v1/object/public/zort-products/${u}`));

// บาร์โค้ดที่ยิงได้จากมือถือ เติมกลับเข้า PD ให้สินค้าที่ PD ยังไม่มีบาร์โค้ด
// (patch_only: sku ไม่มีใน PD = ข้าม ไม่สร้างใหม่ · pd-api ไม่ทับของเดิม) — พลาดไม่บล็อกงานซื้อ
async function backfillBarcode(sku, name, barcode) {
  if (!sku || !name || !barcode) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await fetch('/api/pd/create-product', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ sku, name, barcode, patch_only: true }),
    });
  } catch { /* PD ล่มชั่วคราว — ข้าม */ }
}

function poAmounts(po) {
  const items = po.purchase_order_items ?? [];
  const foreign = items.reduce((t, i) => t + Number(i.quantity || 0) * Number(i.unit_price_foreign || 0), 0);
  const thb = items.reduce((t, i) => t + Number(i.quantity || 0) * Number(i.provisional_unit_cost || 0), 0);
  const isForeign = foreign > 0 && po.currency && po.currency !== 'THB';
  return {
    isForeign,
    main: isForeign ? `${CUR_SYM[po.currency] ?? po.currency}${fmt(foreign)}` : `฿${fmt(po.total_amount)}`,
    thb: isForeign ? `≈ ฿${fmt(thb || po.total_amount)}` : null,
    qty: items.reduce((t, i) => t + Number(i.quantity || 0), 0) || Number(po.total_qty || 0),
    count: items.length,
  };
}

// Accounting CSV — same contract as public.v_accounting_purchases (see ACCOUNTING_API.md)
const ACC_COLS = ['received_date', 'po_no', 'pi_reference', 'supplier', 'currency', 'fx_rate',
  'sku', 'product_name', 'brand', 'qty', 'unit_landed_cost_thb', 'line_total_thb',
  'adjustments_thb', 'landed_total_thb', 'note', 'receiving_line_id'];

async function exportAccountingCsv(from, to, setErr) {
  const { data, error } = await supabase.from('v_accounting_purchases').select('*')
    .gte('received_date', from).lte('received_date', to).order('received_date');
  if (error) { setErr(error.message); return; }
  if (!data?.length) { setErr(`ไม่มีรายการรับของช่วง ${from} – ${to}`); return; }
  const esc = v => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = '﻿' + [ACC_COLS.join(','), ...data.map(r => ACC_COLS.map(c => esc(r[c])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `sourcepoint-accounting-${from}_${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PurchaseOrders() {
  const { hasCostAccess } = useAuth();
  const [pos, setPos] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);   // PO in detail drawer
  const [printPo, setPrintPo] = useState(null);
  const [openQb, setOpenQb] = useState(null);     // ใบซื้อด่วนที่กางดูอยู่
  const [printQb, setPrintQb] = useState(null);
  const [preview, setPreview] = useState(null);   // { kind:'po'|'qb', doc, mode:'delivery'|'full' }
  const [jpgBusy, setJpgBusy] = useState(false);  // กำลัง render ใบเป็นรูป JPG
  const [imgBySku, setImgBySku] = useState(new Map());   // รูปสินค้าสำหรับใบพิมพ์

  // เซฟใบในกรอบดูตัวอย่างเป็นรูป JPG ทั้งใบ (Purim ขอ 1 ส.ค. — ส่งไลน์/แนบแชทได้เลย)
  // useCORS: รูปสินค้าจาก Supabase storage ส่ง Access-Control-Allow-Origin:* อยู่แล้ว canvas ไม่ taint
  async function saveJpg() {
    if (jpgBusy || !preview) return;
    const node = document.querySelector('.pv-page .print-sheet');
    if (!node) return;
    setJpgBusy(true);
    try {
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const a = document.createElement('a');
      const base = preview.kind === 'qb' ? (preview.doc.receipt_no || 'quickbuy') : preview.doc.po_no;
      a.download = `${base}${preview.mode === 'delivery' ? '-delivery-note' : ''}.jpg`;
      a.href = canvas.toDataURL('image/jpeg', 0.92);
      a.click();
    } catch (e) {
      window.alert(`สร้างรูปไม่สำเร็จ: ${e?.message ?? e}`);
    } finally {
      setJpgBusy(false);
    }
  }
  const [drafts, setDrafts] = useState([]);       // ร่างที่ยังทำไม่เสร็จ (ส่วนใหญ่ยิงมาจากมือถือ)
  const [qbNew, setQbNew] = useState(null);       // { draft } — ลิ้นชักสร้าง/ทำร่างต่อ
  const [loadErr, setLoadErr] = useState('');
  const [qbs, setQbs] = useState([]);   // ใบซื้อด่วน (QUICK BUY / iPhone) — คนละตารางกับ PO แต่โชว์รวมที่นี่
  const today = thToday();   // วันไทย (เดิม toISOString = วันที่ UTC เพี้ยนช่วง 00:00–07:00)
  const [accFrom, setAccFrom] = useState(today.slice(0, 8) + '01');
  const [accTo, setAccTo] = useState(today);
  const [accErr, setAccErr] = useState('');

  async function load() {
    const { data, error } = await supabase.from('purchase_orders').select(`
      id, po_no, reference, status, currency, fx_rate, created_at, expected_at,
      total_qty, total_amount, archived, brand_name, notes, pi_file_path,
      zort_synced_at, zort_po_number, zort_sync_error, zort_warehouse_code, pd_synced_at,
      wh_released_at, wh_released_by,
      supplier:suppliers(id, company_name, currency, contact),
      purchase_order_items(id, product_id, sku_snapshot, product_name_snapshot, brand_name_snapshot,
        supplier_product_code, quantity, unit_price, unit_price_foreign, provisional_unit_cost, received_qty,
        line_type, fee_category, product:products(sku))
    `).eq('archived', false).order('created_at', { ascending: false }).limit(500);
    if (error) setLoadErr(error.message);
    setPos(data ?? []);
    loadArchivedCount();
    return data ?? [];
  }
  useEffect(() => { load(); }, []);

  // ---- คลังเอกสาร: ใบที่กด "เก็บ" ไปแล้ว — เปิดดู/กู้คืนได้ (Purim ถาม 1 ส.ค. "ซ่อนแล้วดูตรงไหน") ----
  const [archivedCount, setArchivedCount] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedRows, setArchivedRows] = useState(null);
  async function loadArchivedCount() {
    const { count } = await supabase.from('purchase_orders')
      .select('id', { count: 'exact', head: true }).eq('archived', true);
    setArchivedCount(count ?? 0);
  }
  async function loadArchivedList() {
    const { data } = await supabase.from('purchase_orders')
      .select('id, po_no, reference, status, created_at, total_amount, currency, notes, supplier:suppliers(company_name), brand_name')
      .eq('archived', true).order('created_at', { ascending: false }).limit(100);
    setArchivedRows(data ?? []);
  }
  async function toggleArchived() {
    if (showArchived) { setShowArchived(false); return; }
    await loadArchivedList();
    setShowArchived(true);
  }
  async function restorePo(row) {
    if (!window.confirm(`กู้คืนใบ ${row.po_no} กลับเข้าลิสต์?\n(ใบจะกลับมานับ "กำลังเข้า" ตามสถานะเดิม)`)) return;
    const { error } = await supabase.from('purchase_orders').update({ archived: false }).eq('id', row.id);
    if (error) { window.alert(error.message); return; }
    setArchivedRows(rs => (rs ?? []).filter(r => r.id !== row.id));
    await load();
  }
  // ลบถาวร (Purim ขอ 1 ส.ค. เพื่อความเป็นระเบียบ) — เฉพาะใบในคลังเอกสารที่ติ๊กเลือก
  // เกราะ 2 ชั้น: รายการสินค้า cascade ตามใบ · ใบที่มีประวัติรับของ (stock.receivings) DB จะปฏิเสธการลบเอง
  // เพราะเลขบัญชี (v_accounting_purchases) เลี้ยงจากประวัติรับ — ใบพวกนั้นเก็บในคลังได้อย่างเดียว
  const [archSel, setArchSel] = useState({});
  const archSelIds = Object.keys(archSel).filter(id => archSel[id]);
  async function deleteSelectedArchived() {
    const rows = (archivedRows ?? []).filter(r => archSel[r.id]);
    if (!rows.length) return;
    const names = rows.map(r => r.po_no).join(', ');
    if (!window.confirm(`ลบถาวร ${rows.length} ใบ: ${names}\n\n⚠️ ลบแล้วกู้คืนไม่ได้เลย (ต่างจาก "เก็บ" ที่กู้ได้)\nใบที่มีประวัติรับของ/ผูกบัญชี ระบบจะปฏิเสธการลบให้เอง\n\nยืนยันลบถาวร?`)) return;
    const failed = [];
    for (const r of rows) {
      const { error } = await supabase.from('purchase_orders').delete().eq('id', r.id);
      if (error) failed.push(`${r.po_no} — ${/foreign key|violates/i.test(error.message) ? 'มีประวัติรับของผูกอยู่ ลบไม่ได้ (คงไว้ในคลัง)' : error.message}`);
    }
    setArchSel({});
    await loadArchivedList();
    loadArchivedCount();
    if (failed.length) window.alert(`ลบไม่สำเร็จ ${failed.length} ใบ:\n${failed.join('\n')}`);
  }

  // ใบซื้อด่วนอยู่ในฐาน PD คนละที่กับใบสั่งซื้อ — ดึงมาโชว์รวมกันจะได้ตอบได้ที่เดียวว่า "สั่งอะไรไปบ้าง"
  async function loadQbs() {
    if (!hasCostAccess) return [];   // /api/pd/quick เปิดเฉพาะ role ต้นทุน
    try {
      const o = await authFetch('/api/pd/quick', { method: 'GET' });
      const rows = (o.rows || []).filter(b => b.status !== 'cancelled');
      setQbs(rows);
      return rows;
    } catch { return []; }           // เงียบ — ใบสั่งซื้อยังดูได้ปกติ
  }
  useEffect(() => { loadQbs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hasCostAccess]);

  async function loadDrafts() {
    if (!hasCostAccess) return;
    try { const o = await authFetch('/api/pd/drafts', { method: 'GET' }); setDrafts(o.drafts || []); }
    catch { /* เงียบ */ }
  }
  useEffect(() => { loadDrafts(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hasCostAccess]);
  async function delDraft(id) {
    if (!window.confirm('ลบร่างนี้?')) return;
    try { await authFetch('/api/pd/drafts', { method: 'DELETE', body: JSON.stringify({ id }) }); await loadDrafts(); }
    catch (e) { setLoadErr(e.message); }
  }

  const shown = useMemo(() => {
    if (!pos) return [];
    const rows = [
      ...pos.map(po => ({ kind: 'po', at: po.created_at, po })),
      ...qbs.map(b => ({ kind: 'qb', at: b.created_at, qb: b })),
    ].sort((a, b) => new Date(b.at) - new Date(a.at));
    const needle = cleanText(q).trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(r => {
      const hay = (r.kind === 'po'
        ? [r.po.po_no, r.po.reference, r.po.brand_name, r.po.supplier?.company_name, r.po.status,
           ...(r.po.purchase_order_items ?? []).flatMap(i => [i.sku_snapshot, i.product_name_snapshot, i.supplier_product_code])]
        : [r.qb.receipt_no, r.qb.supplier, r.qb.note, 'ซื้อด่วน',
           ...(r.qb.lines ?? []).flatMap(l => [l.sku, l.name])]
      ).filter(Boolean).join(' ').toLowerCase();
      return needle.split(/\s+/).every(w => hay.includes(w));
    });
  }, [pos, qbs, q]);

  useEffect(() => {
    const skus = [...new Set([
      ...(pos ?? []).flatMap(p => (p.purchase_order_items ?? [])
        .filter(i => i.line_type === 'product')
        .map(i => i.product?.sku || i.sku_snapshot)),
      ...qbs.flatMap(b => (b.lines ?? []).map(l => l.sku)),
    ].filter(Boolean))];
    if (!skus.length) return;
    let dead = false;
    (async () => {
      const m = new Map();
      for (let i = 0; i < skus.length; i += 200) {   // กัน URL ยาวเกินถ้าใบเยอะ
        const { data } = await supabase.from('ib_pd_products').select('sku, image_url').in('sku', skus.slice(i, i + 200));
        (data ?? []).forEach(r => { if (r.image_url) m.set(r.sku, r.image_url); });
      }
      if (!dead) setImgBySku(m);
    })();
    return () => { dead = true; };
  }, [pos, qbs]);

  // สั่งพิมพ์: ต้องรอรูปโหลดจบก่อน ไม่งั้นรูปที่ยังมาไม่ทันจะพิมพ์ออกมาเป็นช่องว่าง
  // (ของเดิมรอ 120ms ตายตัว — แคชอุ่นตอนเทสต์ก็ผ่าน แต่คนพิมพ์ครั้งแรกจะรูปหาย)
  useEffect(() => {
    if (!preview) return;
    const onKey = e => { if (e.key === 'Escape') setPreview(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  useEffect(() => {
    const job = printPo || printQb;
    if (!job) return;
    let dead = false;
    (async () => {
      const skus = printPo
        ? (printPo.po.purchase_order_items ?? []).filter(i => i.line_type === 'product').map(i => i.product?.sku || i.sku_snapshot)
        : (printQb.qb.lines ?? []).map(l => l.sku);
      await preloadImages(skus.map(k => imgBySku.get(k)));
      await afterPaint();
      if (dead) return;
      window.print();
      setPrintPo(null); setPrintQb(null);
    })();
    return () => { dead = true; };
  }, [printPo, printQb, imgBySku]);


  return (
    <>
      <div className="hero">
        <div className="eyebrow">ใบสั่งซื้อ · Purchase orders</div>
        <h1>สั่งอะไรไป<br />ถึงไหนแล้ว</h1>
        <p className="sub">
          รวมทุกใบที่สั่งไป — <b>ใบสั่งซื้อ</b> จากใบ PI (มีเลขที่ PI · สกุลเงิน · ค่าขนส่งเฉลี่ยเข้าต้นทุน)
          และ <b>ใบซื้อด่วน</b> ที่ยิงจาก QUICK BUY / iPhone (บาทล้วน ไม่มีค่าขนส่ง) ·
          ค้นหาได้จากเลขที่ / ซัพพลายเออร์ / SKU / ชื่อสินค้า และพิมพ์เป็นเอกสารได้
        </p>
      </div>

      <div className="prod-search">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="#0A0A0A" strokeWidth="1.6"/><path d="M16.5 16.5L21 21" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
        <input value={q} onChange={e => setQ(e.target.value)}
               placeholder="ค้นหา — เลขที่ PO / เลขที่ PI / ซัพพลายเออร์ / SKU / ชื่อสินค้า" />
      </div>

      {hasCostAccess && (
        <div className="acc-export">
          <span className="lbl"><span className="th">ส่งบัญชี</span><span className="en">Accounting export</span></span>
          <input className="under mono" type="date" value={accFrom} onChange={e => setAccFrom(e.target.value)} />
          <span className="arw">→</span>
          <input className="under mono" type="date" value={accTo} onChange={e => setAccTo(e.target.value)} />
          <button className="btn-create" onClick={() => { setAccErr(''); exportAccountingCsv(accFrom, accTo, setAccErr); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 4v12M12 16l-4-4M12 16l4-4M4 20h16" stroke="currentColor" strokeWidth="1.6"/></svg>
            ดาวน์โหลด CSV
          </button>
          <span className="sd-empty" style={{ margin: 0 }}>{accErr || 'ตามรายการรับของ + landed cost จริง · แอปบัญชีใหม่อ่าน view เดียวกันผ่าน API'}</span>
        </div>
      )}

      {hasCostAccess && (
        <div className="qb-quickbar">
          <button className="add-sup" onClick={() => setQbNew({ draft: null })}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.7" /></svg>
            ซื้อด่วนใบใหม่
          </button>
          <span className="sd-empty" style={{ margin: 0 }}>
            ซื้อเติมในไทย จ่ายบาท — ยิงบาร์โค้ด/พิมพ์ SKU แล้วกดสร้างใบ · ของนอกที่มีใบ PI ให้ใช้ “by PI”
          </span>
        </div>
      )}

      {drafts.length > 0 && (
        <div className="qb-drafts" style={{ borderTop: 'none', paddingTop: 0, marginBottom: 26 }}>
          <h5>ร่างที่ยังทำไม่เสร็จ ({drafts.length}) · ส่วนใหญ่ยิงมาจากมือถือ</h5>
          <div className="qb-dtable">
            {drafts.map(d => {
              const pl = d.payload || {};
              const items = Array.isArray(pl.cart) ? pl.cart : [];
              const unk = Array.isArray(pl.unknown) ? pl.unknown : [];
              const t = items.reduce((x, l) => x + (Number(l.qty) || 0) * (parseFloat(l.unit_cost) || 0), 0);
              return (
                <div className="qb-drow" key={d.id}>
                  <div style={{ minWidth: 0 }}>
                    <button className="qb-dopen" onClick={() => setQbNew({ draft: d })}>{d.title || '(ไม่มีชื่อ)'}</button>
                    <div className="qb-sub">{[pl.supplier, unk.length ? `ยังไม่รู้จัก ${unk.length}` : '', pl.note,
                      pl.scan_meta?.source === 'mobile-scan' ? 'ยิงจากมือถือ' : '', qbWho(d.created_by)].filter(Boolean).join(' · ')}</div>
                  </div>
                  <span className="num">{items.length + unk.length}</span>
                  <span className="num mono">฿{fmt(t, 2)}</span>
                  <span className="qb-dim">{fmtDT(d.updated_at)}</span>
                  <button className="qb-rm" onClick={() => delDraft(d.id)} aria-label="ลบร่าง">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" /></svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="po-head">
        <span className="first">เลขที่ · PO</span>
        <span className="first">ซัพพลายเออร์ · Supplier</span>
        <span className="first">PI Number</span>
        <span>รายการ</span>
        <span>มูลค่า</span>
        <span>สกุลเงิน</span>
        <span className="lay">สถานะ</span>
        <span className="first">หมายเหตุ · Note</span>
        <span></span>
      </div>

      {loadErr && <p className="err-note">โหลดไม่สำเร็จ: {loadErr}</p>}
      {pos && shown.length === 0 && !loadErr && (
        <div className="list-empty">{q ? `ไม่พบ "${q}"` : 'ยังไม่มีใบสั่งซื้อ — สร้างได้จากหน้า “by PI”'}</div>
      )}

      <div className="sup-list">
        {shown.map(row => {
          // ── ใบซื้อด่วน: เอกสารคนละชนิด (ไม่มี PI/สกุลเงิน/ค่าขนส่ง) กดแล้วเด้งไปแก้ที่หน้า QUICK BUY ──
          if (row.kind === 'qb') {
            const b = row.qb;
            const qty = (b.lines ?? []).reduce((t, l) => t + (Number(l.qty) || 0), 0);
            const chip = b.status === 'received' ? ['รับครบ', 'ok'] : ['กำลังมา', 'amb']; // คำเดียวกับใบ PO
            return (
              <div className="po-card qb-card" key={b.id} onClick={() => setOpenQb(b)}>
                <div>
                  <div className="po-ref mono" style={{ fontSize: 15 }}>{b.receipt_no || 'ใบซื้อด่วน'}<span className="qb-tag">ซื้อด่วน</span></div>
                  <div className="po-date">{[b.expected_at ? `ของถึง ${fmtDate(b.expected_at)}` : '', fmtDate(b.created_at)].filter(Boolean).join(' · ')}</div>
                </div>
                <div className="po-sup">{b.supplier || '—'}</div>
                <div className="po-pi mono" title={(b.attachments ?? []).map(a => a.name).join('\n')}>
                  {(b.attachments ?? []).length > 0 ? `📎 ${b.attachments.length} ไฟล์` : ''}
                </div>
                <div className="sup-num">{(b.lines ?? []).length}</div>
                <div style={{ textAlign: 'right' }}>
                  <div className="po-amt">฿{fmt(b.total_thb, 2)}</div>
                  <div className="po-thb">{fmt(qty)} ชิ้น</div>
                </div>
                <div className="po-cur mono">THB</div>
                <div className="sup-lay"><span className={`po-chip ${chip[1]}`}>{chip[0]}</span></div>
                <div className="po-note">{b.note || ''}</div>
                <span className="arrow sm"><svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg></span>
              </div>
            );
          }
          const po = row.po;
          const a = poAmounts(po);
          const chip = PO_CHIP[po.status] ?? [po.status, 'out'];
          return (
            <div className="po-card" key={po.id} onClick={() => setOpen(po)}>
              <div>
                <div className="po-ref" style={{ fontSize: 15 }}>{po.po_no}</div>
                <div className="po-date">{fmtDate(po.created_at)}</div>
              </div>
              <div className="po-sup">{po.supplier?.company_name ?? po.brand_name ?? '—'}</div>
              {/* โชว์เฉพาะใบที่มีไฟล์ PI แนบจริง — เลขที่ระบบสร้างเอง (เช่น INAUTO…) ไม่ใช่เอกสาร PI */}
              <div className="po-pi mono">{po.pi_file_path ? (po.reference || '') : ''}</div>
              <div className="sup-num">{a.count || a.qty}</div>
              <div style={{ textAlign: 'right' }}>
                <div className="po-amt">{a.main}</div>
                {a.thb && <div className="po-thb">{a.thb}</div>}
              </div>
              <div className="po-cur mono">{(po.currency || 'THB').toUpperCase()}</div>
              <div className="sup-lay"><span className={`po-chip ${chip[1]}`}>{chip[0]}</span></div>
              <div className="po-note">{po.notes || ''}</div>
              <span className="arrow sm"><svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg></span>
            </div>
          );
        })}
      </div>

      {/* คลังเอกสาร — ใบที่กด "เก็บ" แล้ว: ดูย้อนหลัง + กู้คืน + ติ๊กเลือกลบถาวรได้ (ใบผูกบัญชีลบไม่ผ่าน) */}
      {archivedCount > 0 && (
        <div className="arch-sec">
          <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
            <button className="mini-edit" onClick={toggleArchived}>
              🗂 คลังเอกสาร — ใบที่เก็บแล้ว ({archivedCount}) {showArchived ? '▲ ซ่อน' : '▼ เปิดดู'}
            </button>
            {showArchived && archSelIds.length > 0 && (
              <button className="mini-edit el-fix" onClick={deleteSelectedArchived}>
                🗑 ลบถาวร ({archSelIds.length} ใบ)
              </button>
            )}
          </div>
          {showArchived && (archivedRows ?? []).map(r => (
            <div className="po-row arch-row" key={r.id} style={{ gridTemplateColumns: '24px 1fr 1fr 1fr auto auto' }}>
              <input type="checkbox" checked={!!archSel[r.id]}
                     onChange={e => setArchSel(s => ({ ...s, [r.id]: e.target.checked }))} />
              <div>
                <div className="po-ref mono" style={{ fontSize: 14 }}>{r.po_no}</div>
                <div className="po-date">{fmtDate(r.created_at)}{r.reference ? ` · PI ${r.reference}` : ''}</div>
              </div>
              <div className="po-sup" style={{ fontSize: 13.5 }}>{r.supplier?.company_name ?? r.brand_name ?? '—'}</div>
              <div className="po-note">{r.notes || ''}</div>
              {/* total_amount เก็บเป็น "บาท" เสมอ (แปลงด้วยเรทตอนบันทึกใบ) — ป้ายต้องเป็น ฿ ทุกใบ */}
              <div className="po-amt">฿{fmt(r.total_amount, 2)}{r.currency && r.currency !== 'THB' ? <span className="po-date" style={{ marginLeft: 6 }}>(ใบ {r.currency})</span> : null}</div>
              <button className="mini-edit" onClick={() => restorePo(r)}>↩ กู้คืน</button>
            </div>
          ))}
          {showArchived && !(archivedRows ?? []).length && <p className="sd-empty">ไม่มีใบในคลังเอกสาร</p>}
        </div>
      )}

      {open && (
        <PoDrawer po={open} canWrite={hasCostAccess} img={imgBySku} onClose={() => setOpen(null)}
                  onPreview={mode => setPreview({ kind: 'po', doc: open, mode })}
                  onSaved={async id => {
                    const fresh = await load();
                    setOpen(fresh.find(p => p.id === id) ?? null);
                  }} />
      )}
      {qbNew && (
        <QbNewDrawer draft={qbNew.draft}
                     onClose={() => setQbNew(null)}
                     onDone={async () => { setQbNew(null); await Promise.all([loadQbs(), loadDrafts()]); }}
                     onDraftSaved={loadDrafts} />
      )}
      {openQb && (
        <QbDrawer qb={openQb} onClose={() => setOpenQb(null)}
                  onPreview={mode => setPreview({ kind: 'qb', doc: openQb, mode })}
                  onChanged={async closeAfter => {
                    const rows = await loadQbs();
                    setOpenQb(closeAfter ? null : (rows.find(b => b.id === openQb.id) ?? null));
                  }} />
      )}
      {preview && (
        <>
          <div className="pv-scrim" onClick={() => setPreview(null)} />
          <div className="pv-wrap" onClick={e => { if (e.target === e.currentTarget) setPreview(null); }}>
            <div className="pv-bar">
              <div className="pv-tabs">
                <button className={preview.mode === 'delivery' ? 'on' : ''} onClick={() => setPreview(p => ({ ...p, mode: 'delivery' }))}>ใบส่งของ (ไม่มีราคา)</button>
                <button className={preview.mode === 'full' ? 'on' : ''} onClick={() => setPreview(p => ({ ...p, mode: 'full' }))}>{preview.kind === 'qb' ? 'ใบซื้อ' : 'ใบสั่งซื้อ'} (มีราคา)</button>
              </div>
              <div className="pv-act">
                <button className="ghost-btn" onClick={saveJpg} disabled={jpgBusy}>
                  {jpgBusy ? 'กำลังสร้างรูป…' : 'Save JPG'}
                </button>
                <button className="add-sup" style={{ padding: '11px 20px', fontSize: 13 }}
                        onClick={() => {
                          const { kind, doc, mode } = preview;
                          setPreview(null);
                          if (kind === 'qb') setPrintQb({ qb: doc, mode }); else setPrintPo({ po: doc, mode });
                        }}>พิมพ์ใบนี้</button>
                <button className="ghost-btn" onClick={() => setPreview(null)}>ปิด</button>
              </div>
            </div>
            <div className="pv-page" onClick={e => { if (e.target === e.currentTarget) setPreview(null); }}>
              {preview.kind === 'qb'
                ? <QbPrintSheet qb={preview.doc} delivery={preview.mode === 'delivery'} img={imgBySku} />
                : <PoPrintSheet po={preview.doc} delivery={preview.mode === 'delivery'} img={imgBySku} />}
            </div>
          </div>
        </>
      )}
      {/* รอรูปโหลดก่อนพิมพ์อาจกินเวลา 1-2 วิ — บอกให้รู้ว่ากำลังทำงาน ไม่ใช่กดไม่ติด */}
      {(printPo || printQb) && (
        <div className="print-wait">
          <span className="pw-dot" />กำลังเตรียมใบพิมพ์ (โหลดรูปสินค้า)…
        </div>
      )}
      {printPo && <PoPrintSheet po={printPo.po} delivery={printPo.mode === 'delivery'} img={imgBySku} />}
      {printQb && <QbPrintSheet qb={printQb.qb} delivery={printQb.mode === 'delivery'} img={imgBySku} />}
    </>
  );
}

const ADJ_TYPES = [
  ['duty', 'ภาษีนำเข้า (duty)'],
  ['freight', 'ค่าขนส่ง (freight)'],
  ['fx_correction', 'ปรับอัตราแลกเปลี่ยน'],
  ['correction', 'ปรับปรุงอื่น ๆ'],
];

function PoDrawer({ po, canWrite, img, onClose, onPreview, onSaved }) {
  // รูปสินค้าจาก PD (Map sku→url ที่ parent โหลดไว้สำหรับใบพิมพ์อยู่แล้ว)
  const thumbOf = sku => img?.get((sku || '').trim()) || null;
  const { profile } = useAuth();
  const a = poAmounts(po);
  const sym = CUR_SYM[po.currency] ?? '';
  const items = po.purchase_order_items ?? [];
  const [ref, setRef] = useState(po.reference ?? '');
  const [fx, setFx] = useState(po.fx_rate != null ? String(po.fx_rate) : '');
  // ใบสกุลต่างประเทศที่ยังไม่มีเรท → เติม "เรทกลาง" ให้ในช่อง (ยังไม่บันทึกจนกว่าจะกดบันทึก) — Purim 1 ต.ค. 69
  const fxTable = useFxDefaults();
  const poIsThb = (po.currency || 'THB').toUpperCase() === 'THB';
  const fxDef = poIsThb ? null : fxDefaultOf(po.currency, fxTable);
  const [fxFromDefault, setFxFromDefault] = useState(false);
  useEffect(() => {
    if (!poIsThb && po.fx_rate == null && fxDef != null && !String(fx).trim()) { setFx(String(fxDef)); setFxFromDefault(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fxDef]);
  const [eta, setEta] = useState(po.expected_at ?? '');
  const [status, setStatus] = useState(po.status);
  // วันที่สั่งซื้อ = วันไทยของ created_at (เดิม slice ISO = วันที่ UTC — ใบที่สร้าง 00:00–07:00 โชว์เป็นเมื่อวาน)
  const poDay = po.created_at ? thDay(po.created_at) : '';
  const [orderDate, setOrderDate] = useState(poDay);
  const [noteTxt, setNoteTxt] = useState(po.notes ?? '');   // หมายเหตุใบ — โชว์ในลิสต์ให้อ่านเร็ว
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // receiving + landed cost (stock schema — cost roles only via RLS)
  const [mode, setMode] = useState('view');            // 'view' | 'receive' | 'editlines'
  const [editRows, setEditRows] = useState([]);        // working copies while editing lines
  const [picker, setPicker] = useState(null);          // rowKey whose product-search panel is open
  const [createFor, setCreateFor] = useState(null);    // rowKey ที่กำลังสร้างสินค้าใหม่มาผูกแทนของขยะ (SKU N/A จากการ import)
  const [claims, setClaims] = useState([]);            // ประวัติส่งมอบสินค้าเครม (pd.claim_handovers ของใบนี้)
  const [claimForm, setClaimForm] = useState({});      // poi.id → { name, qty } ฟอร์มส่งมอบ
  const [pdCosts, setPdCosts] = useState(null);        // sku → ราคาซื้อล่าสุด (฿) จาก PD — โหลดตอนเข้าโหมดแก้ไข
  const [receipts, setReceipts] = useState([]);
  const [adjs, setAdjs] = useState([]);
  const [rcv, setRcv] = useState({});                  // poi.id → { qty, cost }
  const [rcvNote, setRcvNote] = useState('');
  const [stockInMsg, setStockInMsg] = useState(null); // ผลเติมสต๊อกเข้าคลังหลังรับของ {ok, text}
  const [rcvWh, setRcvWh] = useState('');             // คลังที่ของเข้า (เลือกในฟอร์มรับของ)
  const [whList, setWhList] = useState(null);        // รายชื่อคลังของเราเอง wh.branches (null = กำลังโหลด)
  const [adjForm, setAdjForm] = useState({ type: 'duty', amount: '', note: '' });

  const outstanding = i => Number(i.quantity || 0) - Number(i.received_qty || 0);
  const totalOutstanding = items.reduce((t, i) => t + Math.max(0, outstanding(i)), 0);
  const canReceive = canWrite && totalOutstanding > 0 && !['cancelled', 'draft'].includes(po.status);

  async function loadStock() {
    const { data: rs } = await supabase.schema('stock').from('receivings')
      .select('id, received_at, note, receiving_lines(id, purchase_order_item_id, product_id, qty, unit_cost)')
      .eq('purchase_order_id', po.id).order('received_at', { ascending: false });
    setReceipts(rs ?? []);
    const lineIds = (rs ?? []).flatMap(r => (r.receiving_lines ?? []).map(l => l.id));
    if (lineIds.length) {
      const { data: ad } = await supabase.schema('stock').from('cost_adjustments')
        .select('id, adjustment_type, amount_thb, note, created_at, receiving_line_id')
        .in('receiving_line_id', lineIds).order('created_at', { ascending: false });
      setAdjs(ad ?? []);
    } else setAdjs([]);
  }
  useEffect(() => { loadStock(); }, [po.id]);

  // ---- เก็บใบเข้าคลังเอกสาร (archive) — ทางลบใบซ้ำ/ใบขยะที่ปลอดภัย กู้คืนได้ ----
  // วิวทุกตัวกรอง archived=false อยู่แล้ว (ลิสต์ + pd.incoming_stock) — สต๊อก/ประวัติไม่ขยับ
  async function archivePo() {
    if (busy || !canWrite) return;
    const msg = po.status === 'draft'
      ? `ลบร่าง ${po.po_no}?\n\n· หายจากลิสต์ทันที\n· ร่างนี้ยังไม่เคยนับ "กำลังเข้า" และไม่มีสต๊อกเกี่ยวข้อง\n· ข้อมูลถูกเก็บไว้ในคลังเอกสาร (กู้คืนได้ถ้าต้องการ)`
      : `ยกเลิก / ลบใบ ${po.po_no}?\n\n· หายจากลิสต์ และเลิกนับ "กำลังเข้า" ทันที\n· ข้อมูล/ประวัติไม่ถูกลบ (เก็บเข้าคลังเอกสาร กู้คืนได้ภายหลัง)\n· สต๊อกไม่ขยับ — ของที่รับไปแล้วยังอยู่ครบ`;
    if (!window.confirm(msg)) return;
    setBusy(true); setErr('');
    try {
      const { error } = await supabase.from('purchase_orders')
        .update({ archived: true, notes: `${po.notes ? po.notes + ' · ' : ''}เก็บเข้าคลังเอกสารโดย ${profile?.full_name ?? 'ผู้ใช้'} ${fmtDate(new Date().toISOString())}` })
        .eq('id', po.id);
      if (error) throw error;
      await onSaved(po.id);
      onClose();
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // ---- ราคาซื้อล่าสุดจาก PD (เติมให้ใบที่ราคา 0 เช่นใบจาก by Photos) ----------
  // โหลดตอนเข้าโหมดแก้ไขรายการ — pdCosts: sku → purchase_cost (฿)
  async function loadPdCosts(rowsForSkus) {
    const skus = [...new Set(rowsForSkus.map(r => (r.sku_snapshot || '').trim()).filter(Boolean))];
    if (!skus.length) { setPdCosts({}); return; }
    try {
      const o = await authFetch('/api/pd/costs', { method: 'POST', body: JSON.stringify({ skus }) });
      const m = {};
      Object.values(o.rows ?? {}).forEach(r => { if (r.sku) m[r.sku] = r.purchase_cost != null ? Number(r.purchase_cost) : null; });
      setPdCosts(m);
    } catch { setPdCosts({}); }
  }
  // เติมเฉพาะบรรทัดที่ราคายังเป็น 0 — PD เก็บทุนเป็นบาท: ใบ THB ใช้ตรง ๆ / ใบสกุลอื่นต้องมี FX ก่อน
  function fillFromPd() {
    if (!pdCosts) return;
    const cur = (po.currency || 'THB').toUpperCase();
    if (cur !== 'THB' && !fxNum) {
      setErr(`ใบนี้เป็นสกุล ${cur} แต่ยังไม่ได้ใส่เรท FX — ราคาซื้อใน PD เป็นบาท ต้องมีเรทถึงแปลงกลับเป็น ${cur} ได้ (ปิดหน้าต่างแก้ไข แล้วใส่เรทในใบก่อน)`);
      return;
    }
    let n = 0;
    const next = editRows.map(r => {
      if (r._deleted || r.line_type !== 'product' || r.fee_category === 'claim') return r;
      if ((parseFloat(r.unit) || 0) > 0) return r;
      const cost = Number(pdCosts[(r.sku_snapshot || '').trim()]);
      if (!(cost > 0)) return r;
      n++;
      return { ...r, unit: String(cur === 'THB' ? cost : Math.round((cost / fxNum) * 100) / 100) };
    });
    if (!n) { setErr('ไม่มีบรรทัดราคา 0 ที่ PD มีราคาซื้อล่าสุดให้เติม'); return; }
    setEditRows(next); setErr('');
  }

  // ใช้ราคาซื้อล่าสุด (฿) ตรง ๆ — เปลี่ยนสกุลใบเป็น THB ให้ด้วย (เคสใบ by Photos ที่ไม่มีราคา/เรท)
  // บรรทัดที่กรอกราคาสกุลเดิมไว้แล้วต้องมีเรท FX ถึงแปลงได้ — กันเงินสองสกุลปนในใบเดียว
  async function adoptThbCosts() {
    if (busy || !pdCosts) return;
    const cur = (po.currency || 'THB').toUpperCase();
    const nonZero = editRows.filter(r => !r._deleted && (parseFloat(r.unit) || 0) > 0 && r.fee_category !== 'claim');
    if (nonZero.length && !fxNum) {
      setErr(`มีบรรทัดที่กรอกราคาเป็น ${cur} ไว้แล้ว ${nonZero.length} บรรทัด — ใส่เรท FX ของใบก่อน (เพื่อแปลงเป็นบาท) หรือล้างราคาพวกนั้นเป็น 0 ก่อน`);
      return;
    }
    if (!window.confirm(`เปลี่ยนสกุลใบ ${po.po_no} จาก ${cur} → THB แล้วใช้ราคาซื้อล่าสุด (฿) จาก PD?\n\n· บรรทัดราคา 0 → เติมราคาซื้อล่าสุด (฿)\n· ${nonZero.length ? `บรรทัดที่กรอกไว้แล้ว ${nonZero.length} บรรทัด → แปลงเป็นบาทด้วยเรท ${fxNum}` : 'ไม่มีบรรทัดต้องแปลงเรท'}\n· มูลค่าใบทั้งใบจะเป็นบาทตั้งแต่นี้ไป`)) return;
    setBusy(true); setErr('');
    try {
      const { error } = await supabase.from('purchase_orders')
        .update({ currency: 'THB', fx_rate: null }).eq('id', po.id);
      if (error) throw error;
      setEditRows(rs => rs.map(r => {
        if (r._deleted || r.fee_category === 'claim') return r;
        const u = parseFloat(r.unit) || 0;
        if (u > 0) return { ...r, unit: String(Math.round(u * fxNum * 100) / 100) };
        if (r.line_type !== 'product') return r;
        const cost = Number(pdCosts[(r.sku_snapshot || '').trim()]);
        return cost > 0 ? { ...r, unit: String(cost) } : r;
      }));
      await onSaved(po.id);   // รีเฟรชใบ → หัวช่องเปลี่ยนเป็น ฿ · โหมดแก้ไขยังเปิดอยู่ กดบันทึกเมื่อพร้อม
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // ---- ส่งรายการให้ทีมคลังรับของ (แค่ธงปล่อยใบ ไม่แตะสต๊อก) ------------------
  // Purim แก้รายการจนพอใจก่อนแล้วค่อยกดส่ง — ส่งแล้วยังแก้ต่อได้ ทีมคลังอ่านใบเดียวกันสด ๆ
  async function releaseWh(on) {
    if (busy || !canWrite) return;
    setBusy(true); setErr('');
    try {
      const { error } = await supabase.from('purchase_orders').update(on
        ? { wh_released_at: new Date().toISOString(), wh_released_by: profile?.full_name ?? null }
        : { wh_released_at: null, wh_released_by: null }).eq('id', po.id);
      if (error) throw error;
      await onSaved(po.id);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // ---- ส่งมอบสินค้าเครมให้ลูกค้า -------------------------------------------
  const hasClaims = items.some(i => i.fee_category === 'claim');
  async function loadClaims() {
    if (!hasClaims) { setClaims([]); return; }
    try {
      const o = await authFetch('/api/pd/claim-out', { method: 'POST', body: JSON.stringify({ po_id: po.id, list: true }) });
      setClaims(o.rows ?? []);
    } catch { setClaims([]); }
  }
  useEffect(() => { loadClaims(); }, [po.id, hasClaims]);
  const claimHanded = poiId => claims.filter(c => c.poi_id === poiId).reduce((t, c) => t + Number(c.qty || 0), 0);
  async function handClaim(i) {
    if (busy) return;
    const f = claimForm[i.id] ?? {};
    const qn = parseFloat(f.qty) || 0;
    if (!String(f.name ?? '').trim()) { setErr('ใส่ชื่อลูกค้าที่มารับของก่อน'); return; }
    if (!(qn > 0)) { setErr('จำนวนที่ส่งมอบต้องมากกว่า 0'); return; }
    setBusy(true); setErr('');
    try {
      await authFetch('/api/pd/claim-out', {
        method: 'POST',
        body: JSON.stringify({ po_id: po.id, poi_id: i.id, qty: qn, customer_name: f.name.trim() }),
      });
      setClaimForm(cf => ({ ...cf, [i.id]: { name: '', qty: '' } }));
      await loadClaims();
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  function startReceive() {
    const init = {};
    for (const i of items) {
      const out = outstanding(i);
      if (out > 0) init[i.id] = {
        qty: String(out),
        // ทุน/หน่วย (฿): ทุนตั้งต้นของบรรทัด → ไม่มี = ราคาซัพ × เรทของใบ (หรือเรทกลางที่เติมไว้ในช่อง)
        // เดิมใบเรทว่างรับของไปแบบทุนว่าง = ต้นทุนเฉลี่ยไม่ขยับ (PO-2026-080004, 1 ต.ค. 69)
        cost: i.provisional_unit_cost != null ? String(i.provisional_unit_cost)
          : (i.unit_price_foreign != null && (poIsThb || fxNum > 0)
            ? String(Math.round(Number(i.unit_price_foreign) * (poIsThb ? 1 : fxNum) * 100) / 100) : ''),
      };
    }
    setRcv(init); setRcvNote(''); setMode('receive'); setErr('');
    // คลังที่ของเข้า: ค่าเริ่มต้น = คลังของใบ; รายชื่อคลังดึงจากระบบเราเอง (wh.branches)
    setRcvWh(po.zort_warehouse_code || 'W0001');
    if (whList === null) {
      (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const res = await fetch('/api/pd/warehouses',
            { headers: { Authorization: `Bearer ${session.access_token}` } });
          const out = await res.json().catch(() => ({}));
          setWhList(res.ok && Array.isArray(out.warehouses) ? out.warehouses : []);
        } catch { setWhList([]); }
      })();
    }
  }

  async function confirmReceive() {
    if (busy) return;
    const lines = items
      .map(i => ({ i, f: rcv[i.id] }))
      .filter(({ f }) => f && parseFloat(f.qty) > 0)
      .map(({ i, f }) => ({
        purchase_order_item_id: i.id,
        product_id: i.product_id,
        qty: parseFloat(f.qty),
        unit_cost: parseFloat(f.cost) || null,
        poi: i,
      }));
    if (!lines.length) { setErr('ใส่จำนวนรับอย่างน้อย 1 รายการ'); return; }
    if (lines.some(l => !l.product_id)) { setErr('บางรายการไม่มี product_id — รับไม่ได้'); return; }
    if (!rcvWh.trim()) { setErr('เลือกคลังที่ของเข้าก่อนยืนยัน'); return; }
    setBusy(true); setErr('');
    try {
      const { data: rec, error: rErr } = await supabase.schema('stock').from('receivings')
        .insert({ purchase_order_id: po.id, received_by: profile?.id ?? null, note: rcvNote.trim() || null })
        .select('id').single();
      if (rErr) throw rErr;
      const { error: lErr } = await supabase.schema('stock').from('receiving_lines')
        .insert(lines.map(l => ({
          receiving_id: rec.id,
          purchase_order_item_id: l.purchase_order_item_id,
          product_id: l.product_id,
          qty: l.qty,
          unit_cost: l.unit_cost,
        })));
      if (lErr) throw lErr;
      for (const l of lines) {
        const { error } = await supabase.from('purchase_order_items')
          .update({ received_qty: Number(l.poi.received_qty || 0) + l.qty }).eq('id', l.poi.id);
        if (error) throw error;
      }
      const remaining = items.reduce((t, i) => {
        const got = lines.find(l => l.poi.id === i.id)?.qty ?? 0;
        return t + Math.max(0, outstanding(i) - got);
      }, 0);
      const { error: sErr } = await supabase.from('purchase_orders')
        .update({ status: remaining <= 0 ? 'completed' : 'arrived' }).eq('id', po.id);
      if (sErr) throw sErr;

      // ของเข้าจริงแล้ว → ยิงสต๊อกจริงเข้า WH (Free zone) ที่คลังที่เลือก → PD พร้อมขายเด้งเอง · ไม่แตะ Zort
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch('/api/pd/stock-in', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ po_id: po.id, receiving_id: rec.id, warehouse_code: rcvWh.trim() }),
        });
        const out = await res.json().catch(() => ({}));
        if (res.ok && out.ok) {
          setStockInMsg({ ok: true, text: `รับเข้าแล้ว — ยิงสต๊อกจริง ${out.received} รายการเข้า Free zone (คลัง ${out.warehouse}) · PD พร้อมขายอัปเดตแล้ว` });
        } else {
          setStockInMsg({ ok: false, text: `เขียนเข้า WH ไม่สำเร็จ: ${out.error || res.status} — ลองกดส่งซ้ำจากลิ้นชักใบ` });
        }
      } catch (e) {
        setStockInMsg({ ok: false, text: `เขียนเข้า WH ไม่สำเร็จ: ${e.message ?? e}` });
      }

      setMode('view');
      await loadStock();
      await onSaved(po.id);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // ยกเลิกใบรับของ (ของไม่ตรงใบ ฯลฯ): PD ถอนของออกจากคลังก่อน สำเร็จแล้วค่อยถอนฝั่งเรา
  async function cancelReceive(receivingId) {
    if (busy) return;
    if (!window.confirm('ยกเลิกใบรับของนี้?\n\n• ของที่เคยรับเข้าคลังจะถูกถอนออก (ลบจำนวนเดิมออกจาก Free zone)\n• จำนวนรับของรอบนี้จะถูกถอนจากใบสั่งซื้อ\n• ใบใน PD กลับมาสถานะรอรับ\n\nแก้ไขใบเสร็จแล้วค่อยกดรับของใหม่ได้เลย')) return;
    setBusy(true); setErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('เซสชันหมดอายุ — ล็อกอินใหม่');
      const res = await fetch('/api/pd/stock-in-cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ po_id: po.id, receiving_id: receivingId }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || 'ยกเลิกไม่สำเร็จ');
      setStockInMsg({
        ok: true,
        text: `ยกเลิกใบรับแล้ว${out.wh_void?.voided ? ' · ถอนของออกจาก WH เรียบร้อย' : ''} — แก้ไขใบแล้วกดรับของใหม่ได้เลย`,
      });
      await loadStock();
      await onSaved(po.id);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // late duty/freight → allocate across this PO's received lines by value share
  async function addAdjustment() {
    if (busy) return;
    const amount = parseFloat(String(adjForm.amount).replace(/,/g, ''));
    if (!Number.isFinite(amount) || amount === 0) { setErr('ใส่จำนวนเงิน (฿)'); return; }
    const allLines = receipts.flatMap(r => r.receiving_lines ?? []);
    if (!allLines.length) { setErr('ยังไม่มีการรับของ — บันทึกรับของก่อนจึงกระจายค่าใช้จ่ายได้'); return; }
    const totalVal = allLines.reduce((t, l) => t + Number(l.qty) * Number(l.unit_cost || 0), 0);
    setBusy(true); setErr('');
    try {
      const rows = allLines.map((l, idx) => {
        const share = totalVal > 0
          ? (Number(l.qty) * Number(l.unit_cost || 0)) / totalVal
          : Number(l.qty) / allLines.reduce((t, x) => t + Number(x.qty), 0);
        return {
          receiving_line_id: l.id,
          product_id: l.product_id,
          adjustment_type: adjForm.type,
          amount_thb: Math.round(amount * share * 100) / 100,
          note: adjForm.note.trim() || null,
          created_by: profile?.id ?? null,
          _last: idx === allLines.length - 1,
        };
      });
      // rounding drift → dump remainder on the last line
      const allocated = rows.reduce((t, r) => t + r.amount_thb, 0);
      rows[rows.length - 1].amount_thb = Math.round((rows[rows.length - 1].amount_thb + amount - allocated) * 100) / 100;
      const { error } = await supabase.schema('stock').from('cost_adjustments')
        .insert(rows.map(({ _last, ...r }) => r));
      if (error) throw error;
      setAdjForm({ type: 'duty', amount: '', note: '' });
      await loadStock();
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // Push this PO into PD — PD บันทึกใบ + อัปเดตต้นทุนสินค้า (สต๊อกจริงเข้าคลังตอนกดรับของ ไม่แตะ Zort)
  async function pushToPD() {
    if (syncing || !canWrite) return;
    setSyncing(true); setErr('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('เซสชันหมดอายุ — ล็อกอินใหม่');
      const res = await fetch('/api/pd/push-po', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ po_id: po.id }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || 'ส่งเข้า PD ไม่สำเร็จ');
      await onSaved(po.id);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setSyncing(false);
    }
  }

  const fxNum = parseFloat(fx) || 0;
  const foreignTotal = items.reduce((t, i) => t + Number(i.quantity || 0) * Number(i.unit_price_foreign || 0), 0);
  const dirty = noteTxt !== (po.notes ?? '') || ref !== (po.reference ?? '') || String(po.fx_rate ?? '') !== fx
    || eta !== (po.expected_at ?? '') || status !== po.status
    || orderDate !== poDay;

  async function save() {
    if (busy || !canWrite) return;
    setBusy(true); setErr('');
    try {
      // ใบสกุลบาท: ราคาในบรรทัด = บาทอยู่แล้ว ห้ามคูณเรทเด็ดขาด (เผลอพิมพ์เรทลงใบบาท = ยอดเพี้ยนทั้งใบ)
      const isThbPo = (po.currency || 'THB').toUpperCase() === 'THB';
      const upd = {
        reference: ref.trim() || null,
        fx_rate: isThbPo ? null : (fxNum || null),
        expected_at: eta || null,
        notes: noteTxt.trim() || null,
        status,
      };
      // backdate: change the order date (PO number stays as issued)
      if (orderDate && orderDate !== poDay) upd.created_at = orderDate;
      // มูลค่าใบเก็บเป็นบาทเสมอ: ใบบาท = ผลรวมบรรทัดตรง ๆ · ใบสกุลนอก = ผลรวม × เรทของใบ
      if (foreignTotal > 0 && (isThbPo || fxNum)) {
        upd.total_amount = Math.round(foreignTotal * (isThbPo ? 1 : fxNum) * 100) / 100;
      }
      const { error } = await supabase.from('purchase_orders').update(upd).eq('id', po.id);
      if (error) throw error;
      if (isThbPo || fxNum) {
        for (const i of items.filter(i => i.unit_price_foreign != null)) {
          const { error: iErr } = await supabase.from('purchase_order_items')
            .update({ provisional_unit_cost: Math.round(i.unit_price_foreign * (isThbPo ? 1 : fxNum) * 100) / 100 })
            .eq('id', i.id);
          if (iErr) throw iErr;
        }
      }
      await onSaved(po.id);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // ---- edit line items ------------------------------------------------------
  // Lines already (partly) received are locked: qty can't drop below received,
  // product/type can't change, can't delete — so the receiving ledger stays sound.
  function startEditLines() {
    setEditRows(items.map(i => ({
      key: i.id,
      id: i.id,
      product_id: i.product_id ?? null,
      sku_snapshot: i.sku_snapshot ?? '',
      product_name_snapshot: i.product_name_snapshot ?? '',
      supplier_product_code: i.supplier_product_code ?? '',
      qty: String(i.quantity ?? ''),
      unit: i.unit_price_foreign != null ? String(i.unit_price_foreign)
          : (i.unit_price != null ? String(i.unit_price) : ''),
      line_type: i.line_type ?? 'product',
      fee_category: i.fee_category ?? 'other',
      received_qty: Number(i.received_qty || 0),
      _deleted: false,
    })));
    setPicker(null); setMode('editlines'); setErr('');
    loadPdCosts(items);   // ราคาซื้อล่าสุดจาก PD — โชว์ใต้ช่องราคา + ปุ่มเติมทั้งใบ
  }
  const setRow = (key, patch) => setEditRows(rs => rs.map(r => r.key === key ? { ...r, ...patch } : r));
  const blankRow = extra => ({
    key: 'new-' + Math.random().toString(36).slice(2, 9), id: null, product_id: null,
    sku_snapshot: '', product_name_snapshot: '', supplier_product_code: '',
    qty: '1', unit: '', line_type: 'product', fee_category: null, received_qty: 0, _deleted: false, ...extra,
  });
  function addProductRow() { const r = blankRow(); setEditRows(rs => [...rs, r]); setPicker(r.key); }
  function addFeeRow() { setEditRows(rs => [...rs, blankRow({ line_type: 'fee', fee_category: 'other' })]); }
  function removeRow(key) {
    setEditRows(rs => {
      const r = rs.find(x => x.key === key);
      if (!r || r.received_qty > 0) return rs;
      return r.id ? rs.map(x => x.key === key ? { ...x, _deleted: true } : x) : rs.filter(x => x.key !== key);
    });
  }
  // picker กลาง (components/ProductPicker) การันตีคืน product ที่มี id ฝั่ง BUY เสมอ
  function pickProduct(key, p) {
    setRow(key, { product_id: p.id, sku_snapshot: p.sku ?? '', product_name_snapshot: p.name, line_type: 'product', fee_category: null });
    setPicker(null);
  }
  const liveRows = editRows.filter(r => !r._deleted);
  const editForeignTotal = liveRows.reduce((t, r) =>
    t + (parseFloat(r.qty) || (r.line_type === 'fee' ? 1 : 0)) * (parseFloat(r.unit) || 0), 0);

  // บรรทัดผิดปกติ — ไฮไลต์แดงพร้อมเหตุผล ให้เคลียร์ได้ก่อนกดบันทึก/รับของ
  // (SKU ขยะแบบ N/A เกิดจาก import PI ที่สร้างสินค้าโดยไม่มีรหัสจริง)
  const skuJunk = s => !String(s ?? '').trim() || /^(n\/?a|-+|\.+|null|none)$/i.test(String(s).trim());
  function rowIssues(r) {
    const out = [];
    const qn = parseFloat(r.qty);
    if (r.line_type === 'product') {
      const isClaim = r.fee_category === 'claim';
      if (!r.product_id) out.push('ยังไม่ได้เลือกสินค้า');
      else if (skuJunk(r.sku_snapshot)) out.push(`SKU ไม่ถูกต้อง (${String(r.sku_snapshot ?? '').trim() || 'ว่าง'}) — สร้างสินค้าจริงแล้วผูกแทน`);
      if (!(qn > 0)) out.push('จำนวนต้องมากกว่า 0');
      else if (qn % 1 !== 0) out.push(`จำนวนเป็นเศษทศนิยม (${r.qty}) — น่าจะ import เพี้ยน`);
      // สินค้าเครมราคา 0 คือถูกต้อง — ไม่ต้องเตือน (และห้ามมีราคาด้วยซ้ำ)
      if (!isClaim && !(parseFloat(r.unit) > 0)) out.push('ราคาซื้อ/หน่วยยังเป็น 0 — ถ้าเป็นของเครม/แถม เปลี่ยนประเภทเป็น "สินค้าเครม"');
      if (isClaim && parseFloat(r.unit) > 0) out.push('สินค้าเครมต้องราคา 0');
    } else if (!r.product_name_snapshot.trim()) {
      out.push('ยังไม่ใส่ชื่อรายการค่าใช้จ่าย');
    }
    return out;
  }

  async function confirmEditLines() {
    if (busy || !canWrite) return;
    // ใบ THB: ราคาในบรรทัดคือบาทอยู่แล้ว → provisional = ตัวเดียวกัน (ไม่งั้นฟอร์มรับของไม่มีต้นทุนตั้งต้น)
    const isThbPo = (po.currency || 'THB').toUpperCase() === 'THB';
    const toThbUnit = u => isThbPo ? Math.round((parseFloat(u) || 0) * 100) / 100
      : (fxNum ? Math.round((parseFloat(u) || 0) * fxNum * 100) / 100 : null);
    // validate
    for (const r of liveRows) {
      const qn = parseFloat(r.qty);
      if (r.line_type === 'product') {
        if (!r.product_id) { setErr('มีบรรทัดสินค้าที่ยังไม่ได้เลือกสินค้า — เลือกสินค้า หรือลบบรรทัดออก'); return; }
        if (!(qn > 0)) { setErr(`จำนวนต้องมากกว่า 0 (${r.product_name_snapshot || r.sku_snapshot})`); return; }
      } else if (!r.product_name_snapshot.trim()) { setErr('ใส่ชื่อรายการค่าใช้จ่ายให้ครบ'); return; }
      if (r.received_qty > 0 && (parseFloat(r.qty) || 0) < r.received_qty) {
        setErr(`ลดจำนวนต่ำกว่าที่รับแล้วไม่ได้ — ${r.product_name_snapshot} รับไปแล้ว ${fmt(r.received_qty)}`); return;
      }
    }
    if (!liveRows.some(r => r.line_type === 'product')) { setErr('ต้องมีสินค้าอย่างน้อย 1 รายการ'); return; }
    setBusy(true); setErr('');
    try {
      const dels = editRows.filter(r => r._deleted && r.id && r.received_qty === 0).map(r => r.id);
      if (dels.length) {
        const { error } = await supabase.from('purchase_order_items').delete().in('id', dels);
        if (error) throw error;
      }
      for (const r of liveRows) {
        const isProd = r.line_type === 'product';
        const qn = parseFloat(r.qty) || (isProd ? 0 : 1);
        const un = parseFloat(r.unit) || 0;
        const row = {
          quantity: qn,
          unit_price_foreign: un,
          provisional_unit_cost: toThbUnit(r.unit),
          line_type: r.line_type,
          fee_category: isProd ? (r.fee_category === 'claim' ? 'claim' : null) : (r.fee_category || 'other'),
          product_id: isProd ? r.product_id : null,
          sku_snapshot: isProd ? (r.sku_snapshot || null) : null,
          product_name_snapshot: r.product_name_snapshot.trim() || r.sku_snapshot || null,
          supplier_product_code: r.supplier_product_code || null,
        };
        const { error } = r.id
          ? await supabase.from('purchase_order_items').update(row).eq('id', r.id)
          : await supabase.from('purchase_order_items').insert({ ...row, purchase_order_id: po.id, unit_price: 0, status: 'ordered' });
        if (error) throw error;
      }
      const totQty = liveRows.filter(r => r.line_type === 'product').reduce((t, r) => t + (parseFloat(r.qty) || 0), 0);
      const upd = { total_qty: totQty };
      const isThb = (po.currency || 'THB').toUpperCase() === 'THB';
      if (isThb) upd.total_amount = Math.round(editForeignTotal * 100) / 100;
      else if (fxNum) upd.total_amount = Math.round(editForeignTotal * fxNum * 100) / 100;
      // (foreign PO with no FX yet → leave total_amount; it recomputes when FX is entered)
      if (po.pd_synced_at) upd.pd_synced_at = null; // lines changed → PD copy stale, prompt re-push
      const { error } = await supabase.from('purchase_orders').update(upd).eq('id', po.id);
      if (error) throw error;
      setMode('view');
      await onSaved(po.id);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim open" onClick={onClose} />
      {/* ตอนแก้ไขรายการ กางลิ้นชักมากลางจอกว้างพอดีเอกสาร — แก้ในลิ้นชักแคบไม่ถนัด */}
      <aside className={`drawer open${mode === 'editlines' ? ' doc-center' : ''}`} aria-hidden="false">
        <div className="drawer-head">
          <div>
            <div className="eyebrow">ใบสั่งซื้อ · Purchase order</div>
            <h2 className="mono" style={{ fontSize: 27 }}>{po.po_no}</h2>
          </div>
          <button className="xbtn" onClick={onClose} aria-label="ปิด">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
          </button>
        </div>
        {mode === 'receive' ? (
        <div className="drawer-body">
          <div className="cost-note" style={{ borderColor: 'var(--ink)', marginTop: 4 }}>
            <span>บันทึกการรับของ — ต้นทุน/หน่วยคือ <b>landed cost (฿)</b> ที่รู้ ณ ตอนรับ
            (ตั้งต้นจากราคา PI × FX) · duty/freight ที่มาทีหลังเพิ่มได้ในหน้าใบสั่งซื้อ</span>
          </div>
          <div className="field" style={{ marginTop: 18 }}>
            <div className="lbl"><span className="th">คลังที่ของเข้า</span><span className="en">Warehouse · Free zone</span></div>
            {whList === null ? (
              <p className="sd-empty" style={{ margin: '4px 0 0' }}>กำลังโหลดรายชื่อคลัง…</p>
            ) : whList.length ? (
              <select className="under" value={rcvWh} onChange={e => setRcvWh(e.target.value)}>
                {!whList.some(w => w.code === rcvWh) && rcvWh && <option value={rcvWh}>{rcvWh}</option>}
                {whList.map(w => <option key={w.code} value={w.code}>{w.code} — {w.name}</option>)}
              </select>
            ) : (
              <input className="under mono" value={rcvWh} onChange={e => setRcvWh(e.target.value)} placeholder="W0001" />
            )}
            <p className="sd-empty" style={{ margin: '6px 0 0' }}>
              เลขจำนวนที่รับจะถูกยิงเข้าสต๊อกจริงที่ bin “Free zone” ของคลังนี้ทันที · PD พร้อมขายอัปเดตตาม
            </p>
          </div>
          {items.filter(i => outstanding(i) > 0).map(i => (
            <div className="rcv-row" key={i.id}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 13.5 }}>{i.product_name_snapshot}</div>
                <div className="po-date mono">{i.sku_snapshot ?? '—'} · ค้างรับ {fmt(outstanding(i))}</div>
              </div>
              <div className="field" style={{ margin: 0 }}>
                <div className="lbl"><span className="th">รับจริง</span></div>
                <input className="under mono" inputMode="decimal" value={rcv[i.id]?.qty ?? ''}
                       onChange={e => setRcv({ ...rcv, [i.id]: { ...rcv[i.id], qty: e.target.value } })} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <div className="lbl"><span className="th">ต้นทุน/หน่วย ฿</span></div>
                <input className="under mono" inputMode="decimal" value={rcv[i.id]?.cost ?? ''}
                       placeholder={i.provisional_unit_cost != null ? String(i.provisional_unit_cost) : '—'}
                       onChange={e => setRcv({ ...rcv, [i.id]: { ...rcv[i.id], cost: e.target.value } })} />
              </div>
            </div>
          ))}
          <div className="field" style={{ marginTop: 22 }}>
            <div className="lbl"><span className="th">หมายเหตุ</span><span className="opt">ไม่บังคับ</span></div>
            <input className="under" value={rcvNote} onChange={e => setRcvNote(e.target.value)}
                   placeholder="เช่น เลขที่ใบขน / รอบส่ง" />
          </div>
          <p className="err-note">{err}</p>
        </div>
        ) : mode === 'editlines' ? (
        <div className="drawer-body">
          <div className="cost-note" style={{ borderColor: 'var(--ink)', marginTop: 4 }}>
            <span>แก้จำนวน / ราคา · เพิ่ม-ลบบรรทัด · เปลี่ยนสินค้าหรือประเภทได้
              · บรรทัดที่รับของแล้วจะถูกล็อก (ลด/ลบไม่ได้)
              · <b>"ราคาซื้อ/หน่วย" = ทุนที่จ่ายซัพพลายเออร์ (ราคาส่ง) ไม่ใช่ราคาขายปลีก</b>
              {po.pd_synced_at ? ' · ใบนี้ส่งเข้า PD แล้ว บันทึกเสร็จต้องกด "ส่งเข้า PD" อีกครั้ง' : ''}</span>
          </div>

          {/* ใบจาก by Photos มักไม่มีราคาบนป้าย — เติมจากราคาซื้อล่าสุดใน PD ได้ทั้งใบ (เฉพาะบรรทัดที่ยัง 0) */}
          {pdCosts && Object.values(pdCosts).some(c => c > 0) && liveRows.some(r => r.line_type === 'product' && r.fee_category !== 'claim' && !(parseFloat(r.unit) > 0)) && (
            <>
              <button className="add-sup" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} onClick={fillFromPd} disabled={busy}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 4v12M12 16l-4-4M12 16l4-4M4 20h16" stroke="currentColor" strokeWidth="1.7"/></svg>
                {(po.currency || 'THB').toUpperCase() === 'THB'
                  ? 'เติมราคาบรรทัดที่ยังเป็น 0 จาก "ซื้อล่าสุด" ใน PD'
                  : `เติมราคาเป็น ${po.currency} จาก "ซื้อล่าสุด" (ต้องมีเรท FX)`}
              </button>
              {/* ราคาซื้อล่าสุดใน PD เป็นบาท — ทางลัด: ใช้เลขบาทตรง ๆ แล้วเปลี่ยนสกุลใบเป็น THB (Purim ขอ 1 ส.ค.) */}
              {(po.currency || 'THB').toUpperCase() !== 'THB' && (
                <button className="add-sup" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={adoptThbCosts} disabled={busy}>
                  ฿ ใช้ราคาซื้อล่าสุด (บาท) ทั้งใบ — เปลี่ยนสกุลใบเป็น THB
                </button>
              )}
            </>
          )}

          {liveRows.map(r => {
            const locked = r.received_qty > 0;
            const isProd = r.line_type === 'product';
            const isClaim = isProd && r.fee_category === 'claim';
            const issues = rowIssues(r);
            return (
              <div className={`edit-line${issues.length ? ' bad' : ''}`} key={r.key}>
                <div className="el-top">
                  <select className={`type-select ${isProd ? '' : 'fee'}`} value={isProd ? (isClaim ? 'claim' : 'product') : (r.fee_category || 'other')}
                          disabled={locked}
                          onChange={e => { const v = e.target.value;
                            if (v === 'product') setRow(r.key, { line_type: 'product', fee_category: null });
                            // สินค้าเครม: ยังเป็นสินค้า (ผูกสินค้า+เข้าสต๊อกได้) แต่ราคาบังคับ 0 ไม่คิดมูลค่าซื้อ
                            else if (v === 'claim') setRow(r.key, { line_type: 'product', fee_category: 'claim', unit: '0' });
                            else setRow(r.key, { line_type: 'fee', fee_category: v, product_id: null, sku_snapshot: '' }); }}>
                    {LINE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.th}</option>)}
                  </select>
                  {!locked && (
                    <button className="el-del" onClick={() => removeRow(r.key)} title="ลบบรรทัดนี้" aria-label="ลบบรรทัด">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 7h12M9 7V5h6v2M8 7l1 12h6l1-12" stroke="currentColor" strokeWidth="1.5"/></svg>
                    </button>
                  )}
                </div>

                {isProd ? (
                  <div className="el-prod">
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      {thumbOf(r.sku_snapshot)
                        ? <div className="thumb"><img src={thumbOf(r.sku_snapshot)} alt="" /></div>
                        : <div className="thumb empty" />}
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13.5 }}>
                          {r.product_name_snapshot || <span style={{ color: 'var(--accent)' }}>ยังไม่ได้เลือกสินค้า</span>}
                        </div>
                        <div className={`po-date mono${skuJunk(r.sku_snapshot) ? ' el-sku-bad' : ''}`}>{r.sku_snapshot || '—'}</div>
                      </div>
                    </div>
                    {!locked && (
                      <div className="el-prod-actions">
                        {(!r.product_id || skuJunk(r.sku_snapshot)) && (
                          <button className="mini-edit el-fix" onClick={() => setCreateFor(r.key)}
                                  title="สร้างสินค้าจริง (มี SKU) แล้วผูกเข้าบรรทัดนี้แทน">
                            ＋ สร้างเป็นสินค้าใหม่
                          </button>
                        )}
                        <button className="mini-edit" onClick={() => setPicker(r.key)}>{r.product_id ? 'เปลี่ยน' : 'เลือกสินค้า'}</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <input className="under" placeholder="ชื่อรายการค่าใช้จ่าย เช่น ค่าขนส่ง / อากร"
                         value={r.product_name_snapshot} onChange={e => setRow(r.key, { product_name_snapshot: e.target.value })} />
                )}

                <div className="el-nums">
                  <div className="field" style={{ margin: 0 }}>
                    <div className="lbl"><span className="th">จำนวน</span>{locked && <span className="opt">รับแล้ว {fmt(r.received_qty)}</span>}</div>
                    <input className="under mono" inputMode="decimal" value={r.qty}
                           onChange={e => setRow(r.key, { qty: e.target.value })} />
                  </div>
                  <div className="field" style={{ margin: 0 }}>
                    <div className="lbl" title="ทุนที่เราจ่ายซัพพลายเออร์ต่อชิ้น (ราคาส่ง/ราคาในใบ PI) — ไม่ใช่ราคาขายปลีกหน้าร้าน">
                      <span className="th">ราคาซื้อ/หน่วย {sym || '฿'}</span>
                      {isClaim ? <span className="opt">เครม — ฟรี</span>
                        : (pdCosts && pdCosts[(r.sku_snapshot || '').trim()] > 0
                            ? <span className="opt">ซื้อล่าสุด ฿{fmt(pdCosts[(r.sku_snapshot || '').trim()], 2)}</span>
                            : <span className="opt">ทุนจากซัพพลายเออร์ ไม่ใช่ราคาขายปลีก</span>)}
                    </div>
                    <input className="under mono" inputMode="decimal" value={isClaim ? '0' : r.unit} disabled={isClaim}
                           onChange={e => setRow(r.key, { unit: e.target.value })} />
                  </div>
                  <div className="el-linetotal mono">
                    {sym}{fmt((parseFloat(r.qty) || (isProd ? 0 : 1)) * (parseFloat(r.unit) || 0), 2)}
                  </div>
                </div>
                {issues.length > 0 && <div className="el-issues">⚠ {issues.join(' · ')}</div>}
                {locked && <div className="fee-note">รับของแล้ว — เปลี่ยนสินค้า/ประเภท หรือลบไม่ได้ (ลดจำนวนต่ำกว่า {fmt(r.received_qty)} ไม่ได้)</div>}
              </div>
            );
          })}

          <div className="el-addrow">
            <button className="add-sup" onClick={addProductRow}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.7"/></svg>
              เพิ่มสินค้า
            </button>
            <button className="add-sup" onClick={addFeeRow}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.7"/></svg>
              เพิ่มค่าใช้จ่าย
            </button>
          </div>

          <div className="handoff" style={{ marginTop: 8 }}>
            <div className="row"><span className="k">มูลค่ารวมใหม่</span><span className="v mono">
              {sym}{fmt(editForeignTotal, 2)}{fxNum ? ` ≈ ฿${fmt(editForeignTotal * fxNum)}` : ''}
            </span></div>
          </div>

          <p className="err-note">{err}</p>
        </div>
        ) : (
        <div className="drawer-body">
          <div className="fx-row" style={{ marginTop: 4 }}>
            <div className="field">
              <div className="lbl"><span className="th">เลขที่ใบ PI</span><span className="en">Reference</span></div>
              <input className="under mono" value={ref} onChange={e => setRef(e.target.value)}
                     placeholder="เลขที่ใบจริงจากซัพพลายเออร์" disabled={!canWrite} />
            </div>
            <div className="field">
              <div className="lbl"><span className="th">สถานะ</span><span className="en">Status</span></div>
              <select className="under" value={status} onChange={e => setStatus(e.target.value)} disabled={!canWrite}>
                {Object.entries(PO_CHIP).map(([k, [th]]) => <option key={k} value={k}>{th}</option>)}
              </select>
            </div>
          </div>
          <div className="fx-row">
            {/* ใบสกุลบาทไม่มีช่องเรท — กันพิมพ์เรทลงใบบาทแล้วยอดถูกคูณซ้ำ (ราคาในใบเป็นบาทอยู่แล้ว) */}
            {(po.currency || 'THB').toUpperCase() === 'THB' ? (
              <div className="field">
                <div className="lbl"><span className="th">สกุลเงินของใบ</span><span className="en">Currency</span></div>
                <div className="mono" style={{ paddingTop: 6, fontSize: 14 }}>THB — บาทล้วน ไม่ต้องใช้เรทแลกเปลี่ยน</div>
              </div>
            ) : (
              <div className="field">
                <div className="lbl"><span className="th">อัตราแลกเปลี่ยน → บาท</span><span className="en">FX → THB</span>
                  <span className="opt">1 {(po.currency || '').toUpperCase()} = ? บาท</span>
                  {fxFromDefault && <span className="opt fx-auto">ค่ากลาง — กดบันทึกเพื่อใช้</span>}</div>
                <input className="under mono" inputMode="decimal" value={fx} onChange={e => { setFx(e.target.value); setFxFromDefault(false); }}
                       placeholder={fxDef ? `ค่ากลาง ${fxDef}` : 'เช่น 0.2350'} disabled={!canWrite} />
              </div>
            )}
            <div className="field">
              <div className="lbl"><span className="th">ของถึงประมาณ</span><span className="en">ETA</span></div>
              <input className="under mono" type="date" value={eta} onChange={e => setEta(e.target.value)} disabled={!canWrite} />
            </div>
          </div>

          <div className="fx-row">
            <div className="field">
              <div className="lbl"><span className="th">วันที่สั่งซื้อ</span><span className="en">Order date</span><span className="opt">ย้อนหลังได้</span></div>
              <input className="under mono" type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} disabled={!canWrite} />
            </div>
            <div />
          </div>

          <div className="field">
            <div className="lbl"><span className="th">หมายเหตุ</span><span className="en">Note</span><span className="opt">โชว์ในลิสต์ใบสั่งซื้อ — จดสั้น ๆ อ่านปราดเดียวรู้เรื่อง</span></div>
            <input className="under" value={noteTxt} onChange={e => setNoteTxt(e.target.value)} disabled={!canWrite}
                   placeholder="เช่น รอบเรือ 15 ส.ค. / มีของเครม 1 ตัว / จ่ายมัดจำแล้ว" />
          </div>

          <div className="handoff" style={{ marginTop: 0 }}>
            <div className="row"><span className="k">ซัพพลายเออร์</span><span className="v" style={{ fontFamily: 'var(--body)' }}>{po.supplier?.company_name ?? po.brand_name ?? '—'}</span></div>
            {po.pi_file_path && (
              <div className="row">
                <span className="k">ไฟล์ใบ PI ต้นฉบับ</span>
                <a className="v" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                   onClick={async () => {
                     const { data } = await supabase.storage.from('sourcepoint-docs')
                       .createSignedUrl(po.pi_file_path, 3600);
                     if (data?.signedUrl) window.open(data.signedUrl, '_blank');
                     else setErr('เปิดไฟล์ไม่ได้ — ตรวจสิทธิ์การเข้าถึง');
                   }}>
                  เปิดไฟล์ ↗
                </a>
              </div>
            )}
            <div className="row"><span className="k">มูลค่า</span><span className="v">
              {a.isForeign ? `${sym}${fmt(foreignTotal)}` : a.main}
              {a.isForeign && (fxNum ? ` ≈ ฿${fmt(foreignTotal * fxNum)}` : ' ≈ ฿— (ใส่ FX)')}
            </span></div>
          </div>

          {/* กล่องนำทาง — Purim 1 ส.ค.: "มีหลายปุ่มไม่กล้ากด" → จุดเดียวบอกชัดว่ากดอะไร
              ปุ่มรับของเข้า (ทางหลัก) อยู่ที่นี่ที่เดียว ปุ่มอื่นลดเป็นปุ่มรอง (ghost) พร้อมคำอธิบาย */}
          {/* ใบร่าง — Purim 7 ก.ย.: "รายการร่าง อยากกดลบหรือยกเลิก ต้องกดตรงไหน" → บอกชัด + ปุ่มลบตัวใหญ่ที่นี่ */}
          {canWrite && po.status === 'draft' && (
            <div className="sd-sec">
              <h5>ขั้นตอนถัดไป · Next step</h5>
              <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
                <span><b>ใบนี้ยังเป็นร่าง</b> — ยังไม่นับเป็น "กำลังเข้า" ใน PD และยังไม่มีสต๊อกเกี่ยวข้อง<br />
                  · จะใช้ต่อ → แก้รายการ/ต้นทุนได้ตามปกติ แล้วเปลี่ยนสถานะเมื่อสั่งจริง<br />
                  · ไม่ใช้แล้ว → กดปุ่มลบร่างด้านล่างนี้ได้เลย (ข้อมูลยังกู้คืนได้)</span>
              </div>
              <button className="ghost-btn danger" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} onClick={archivePo} disabled={busy}>
                🗑 ลบร่างนี้ — หายจากลิสต์ทันที
              </button>
            </div>
          )}

          {canReceive && (
            <div className="sd-sec">
              <h5>ขั้นตอนถัดไป · Next step</h5>
              <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
                <span><b>ของถึงมือแล้ว จะรับเข้าระบบ → กดปุ่มดำข้างล่างนี้ปุ่มเดียวจบ</b> —
                  เลือกคลัง ยืนยันจำนวน แล้วสต๊อกเข้าคลังจริง + ต้นทุนไหลเข้า PD อัตโนมัติ ไม่ต้องกดปุ่มอื่นก่อนเลย<br />
                  · ของยังมาไม่ถึง → ไม่ต้องกดอะไร ใบนี้ขึ้น "กำลังเข้า" ใน PD ให้เองอยู่แล้ว<br />
                  · อยากให้<b>ทีมคลัง</b>เป็นคนกดรับตอนของถึงแทน → ใช้ "ส่งรายการไป WH" (ปุ่มรองด้านล่าง)</span>
              </div>
              <button className="add-sup" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} onClick={startReceive}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 16V4M12 4L7 9M12 4l5 5" stroke="currentColor" strokeWidth="1.7" transform="rotate(180 12 10)"/></svg>
                รับของเข้า ({fmt(totalOutstanding)} ชิ้นค้างรับ)
              </button>
            </div>
          )}

          <div className="sd-sec">
            <h5>ส่งเข้า PD · Products Manager <span style={{ fontWeight: 400, color: 'var(--g1)' }}>— ปุ่มรอง ไม่ใช่การรับของ</span></h5>
            {po.pd_synced_at ? (
              <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
                <span>ส่งเข้า PD แล้ว · {fmtDate(po.pd_synced_at)}
                  {po.zort_warehouse_code ? ` · คลัง ${po.zort_warehouse_code}` : ''} — ใบสั่งซื้อ{' '}
                  <b className="mono">{po.po_no}</b> ขึ้นหน้า "จัดซื้อ" ใน PD แล้ว (รอกดรับ / เติมราคาขายสินค้าใหม่);
                  สต๊อกจริงเข้าคลัง (Free zone) อัตโนมัติตอนกดรับของเข้า → PD พร้อมขายเด้งเอง</span>
              </div>
            ) : po.zort_sync_error ? (
              <div className="cost-note"><span style={{ color: 'var(--accent)' }}>ส่งไม่สำเร็จ:</span> {po.zort_sync_error}</div>
            ) : (
              <p className="sd-empty" style={{ marginTop: 0 }}>
                ยังไม่ได้ส่งเข้า PD — PD จะบันทึกใบ + อัปเดตต้นทุนสินค้า
                แล้วใบจะไปโผล่หน้า "จัดซื้อ" ใน PD ให้กดรับ/เติมราคาขาย;
                สต๊อกจริงเข้าคลัง (Free zone) อัตโนมัติตอนกดรับของเข้า
              </p>
            )}
            {canWrite && po.status !== 'cancelled' && (
              <button className="ghost-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
                      onClick={pushToPD} disabled={syncing}>
                {!syncing && <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 12h13M17 12l-5-5M17 12l-5 5M20 4v16" stroke="currentColor" strokeWidth="1.7"/></svg>}
                {syncing ? 'กำลังส่ง…' : po.pd_synced_at ? 'ส่งซ้ำเข้า PD' : po.zort_sync_error ? 'ลองส่งอีกครั้ง' : 'ส่งเข้า PD'}
              </button>
            )}
          </div>

          {/* ปล่อยใบให้ทีมคลังรับของ — Purim แก้รายการจนพอใจก่อนค่อยส่ง; ส่งแล้วแก้ต่อได้ WH เห็นสด */}
          <div className="sd-sec">
            <h5>ทีมคลัง · Warehouse <span style={{ fontWeight: 400, color: 'var(--g1)' }}>— ปุ่มรอง: ให้ทีมคลังกดรับแทนเรา</span></h5>
            {po.wh_released_at ? (
              <>
                <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
                  <span>ส่งให้ทีมคลังแล้ว · {fmtDate(po.wh_released_at)}{po.wh_released_by ? ` · โดย ${po.wh_released_by}` : ''} —
                    ยังแก้รายการต่อได้ตามปกติ ทีมคลังเห็นเวอร์ชันล่าสุดเสมอ (อ่านใบเดียวกันสด ไม่มีสำเนา)</span>
                </div>
                {canWrite && po.status !== 'cancelled' && (
                  <button className="mini-edit" style={{ marginTop: 10 }} onClick={() => releaseWh(false)} disabled={busy}>
                    ← ดึงกลับจากทีมคลัง (ปิดไม่ให้เห็นชั่วคราว)
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="sd-empty" style={{ marginTop: 0 }}>
                  ยังไม่ได้ส่งให้ทีมคลัง — แก้รายการให้เรียบร้อยก่อน แล้วกดส่ง
                  ใบจะไปขึ้นหน้า "รับของเข้า" ของทีมคลัง (ส่งแล้วยังกลับมาแก้ได้ ทีมคลังเห็นอัปเดตอัตโนมัติ)
                </p>
                {canWrite && po.status !== 'cancelled' && (
                  <button className="ghost-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
                          onClick={() => releaseWh(true)} disabled={busy}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 12h13M17 12l-5-5M17 12l-5 5M20 4v16" stroke="currentColor" strokeWidth="1.7"/></svg>
                    ส่งรายการไป WH ให้ทีมคลังรับของ
                  </button>
                )}
              </>
            )}
          </div>

          <div className="sd-sec">
            <h5 className="sd-h5-row">รายการ ({items.length})
              {canWrite && po.status !== 'cancelled' && (
                <button className="mini-edit" onClick={startEditLines}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M4 20h4L18.5 9.5a2 2 0 00-2.83-2.83L5 17.5V20z" stroke="currentColor" strokeWidth="1.6"/></svg>
                  แก้ไขรายการ
                </button>
              )}
            </h5>
            {items.map(i => (
              <div key={i.id}>
                <div className="po-row" style={{ gridTemplateColumns: '1fr auto auto' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    {i.line_type === 'product' && (thumbOf(i.sku_snapshot)
                      ? <div className="thumb"><img src={thumbOf(i.sku_snapshot)} alt="" /></div>
                      : <div className="thumb empty" />)}
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13.5 }}>{i.product_name_snapshot}</div>
                      <div className="po-date mono">{i.sku_snapshot ?? '—'}{i.supplier_product_code && i.supplier_product_code !== i.sku_snapshot ? ` · ${i.supplier_product_code}` : ''}{i.fee_category === 'claim' && ' · เครม'}</div>
                    </div>
                  </div>
                  <div className="po-amt">× {fmt(i.quantity)}</div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="po-amt">{i.fee_category === 'claim' ? 'เครม · ฟรี' : i.unit_price_foreign ? `${sym}${fmt(i.unit_price_foreign, 2)}` : `฿${fmt(i.unit_price, 2)}`}</div>
                    {Number(i.received_qty) > 0 && (
                      <div className="po-thb" style={outstanding(i) <= 0 ? { color: 'var(--ink)' } : undefined}>
                        รับแล้ว {fmt(i.received_qty)}{outstanding(i) > 0 ? ` / ${fmt(i.quantity)}` : ' ✓'}
                      </div>
                    )}
                  </div>
                </div>
                {/* สินค้าเครมที่รับเข้าคลังแล้ว — ส่งมอบให้ลูกค้า = ตัดสต๊อกออก + จดชื่อคนรับ ระบบถึงตรง */}
                {i.fee_category === 'claim' && Number(i.received_qty) > 0 && (
                  <div className="claim-box">
                    {claims.filter(c => c.poi_id === i.id).map(c => (
                      <div className="po-date" key={c.id}>✓ ส่งมอบ {fmt(c.qty)} ชิ้น — <b>{c.customer_name}</b> · {fmtDate(c.handed_at)}</div>
                    ))}
                    {Number(i.received_qty) - claimHanded(i.id) > 0 ? (canWrite && (
                      <div className="claim-form">
                        <input className="under" placeholder="ชื่อลูกค้าที่มารับของ"
                               value={claimForm[i.id]?.name ?? ''}
                               onChange={e => setClaimForm(cf => ({ ...cf, [i.id]: { qty: String(Number(i.received_qty) - claimHanded(i.id)), ...cf[i.id], name: e.target.value } }))} />
                        <input className="under mono" style={{ width: 64 }} inputMode="decimal"
                               value={claimForm[i.id]?.qty ?? String(Number(i.received_qty) - claimHanded(i.id))}
                               onChange={e => setClaimForm(cf => ({ ...cf, [i.id]: { ...cf[i.id], qty: e.target.value } }))} />
                        <button className="mini-edit" onClick={() => handClaim(i)} disabled={busy}>ลูกค้ารับของแล้ว →</button>
                      </div>
                    )) : (
                      <div className="po-date" style={{ color: 'var(--ink)' }}>ส่งมอบครบแล้ว ✓</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {stockInMsg && (
            <div className="cost-note" style={stockInMsg.ok ? { borderColor: 'var(--ink)' } : undefined}>
              <span>{stockInMsg.ok ? '' : '⚠ '}{stockInMsg.text}</span>
            </div>
          )}

          {receipts.length > 0 && (
            <div className="sd-sec">
              <h5>ประวัติการรับของ ({receipts.length})</h5>
              {receipts.map(r => {
                const qty = (r.receiving_lines ?? []).reduce((t, l) => t + Number(l.qty), 0);
                const val = (r.receiving_lines ?? []).reduce((t, l) => t + Number(l.qty) * Number(l.unit_cost || 0), 0);
                return (
                  <div className="po-row" key={r.id} style={{ gridTemplateColumns: canWrite ? '1fr auto auto auto' : '1fr auto auto' }}>
                    <div>
                      <div className="po-ref">{fmtDate(r.received_at)}</div>
                      {r.note && <div className="po-date">{r.note}</div>}
                    </div>
                    <div className="po-amt">× {fmt(qty)}</div>
                    <div className="po-amt">฿{fmt(val, 2)}</div>
                    {canWrite && (
                      <button className="xbtn" onClick={() => cancelReceive(r.id)} disabled={busy}
                              title="ยกเลิกใบรับนี้ — ถอนของออกจากคลัง + ถอนจำนวนรับ (เผื่อของไม่ตรงใบ)"
                              aria-label="ยกเลิกใบรับ">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6"/></svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {canWrite && (
            <div className="sd-sec">
              <h5>ค่าใช้จ่ายเพิ่มภายหลัง · Duty / freight</h5>
              {adjs.map(ad => (
                <div className="po-row" key={ad.id} style={{ gridTemplateColumns: '1fr auto' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{(ADJ_TYPES.find(t => t[0] === ad.adjustment_type) ?? [null, ad.adjustment_type])[1]}</div>
                    <div className="po-date">{fmtDate(ad.created_at)}{ad.note ? ` · ${ad.note}` : ''}</div>
                  </div>
                  <div className="po-amt">฿{fmt(ad.amount_thb, 2)}</div>
                </div>
              ))}
              <div className="fx-row" style={{ marginTop: 14 }}>
                <div className="field" style={{ marginBottom: 14 }}>
                  <div className="lbl"><span className="th">ประเภท</span></div>
                  <select className="under" value={adjForm.type} onChange={e => setAdjForm({ ...adjForm, type: e.target.value })}>
                    {ADJ_TYPES.map(([k, th]) => <option key={k} value={k}>{th}</option>)}
                  </select>
                </div>
                <div className="field" style={{ marginBottom: 14 }}>
                  <div className="lbl"><span className="th">จำนวนเงิน ฿</span></div>
                  <input className="under mono" inputMode="decimal" value={adjForm.amount}
                         onChange={e => setAdjForm({ ...adjForm, amount: e.target.value })} placeholder="0" />
                </div>
              </div>
              <div className="fx-row">
                <div className="field" style={{ marginBottom: 0 }}>
                  <div className="lbl"><span className="th">หมายเหตุ</span><span className="opt">เช่น Yusen inv.</span></div>
                  <input className="under" value={adjForm.note} onChange={e => setAdjForm({ ...adjForm, note: e.target.value })} />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button className="btn-create" onClick={addAdjustment} disabled={busy}>กระจายเข้าต้นทุน</button>
                </div>
              </div>
              <p className="sd-empty" style={{ marginTop: 10 }}>
                กระจายให้ทุกรายการที่รับแล้วตามสัดส่วนมูลค่า → ต้นทุนเฉลี่ยของสินค้าอัปเดตอัตโนมัติ
              </p>
            </div>
          )}

          {/* เก็บใบเข้าคลังเอกสาร = ทางลบใบซ้ำ/ใบขยะที่ปลอดภัย — ซ่อนจากลิสต์+เลิกนับ "กำลังเข้า" แต่ข้อมูลอยู่ครบ */}
          {canWrite && po.status !== 'draft' && (
            <div className="sd-sec" style={{ borderTop: '1px solid var(--g4)', paddingTop: 16 }}>
              <button className="ghost-btn danger" onClick={archivePo} disabled={busy}>
                🗑 ยกเลิก / ลบใบนี้ — ซ่อนจากลิสต์ · เลิกนับ "กำลังเข้า" · ข้อมูลเก็บไว้ในคลังเอกสาร กู้คืนได้
              </button>
            </div>
          )}
        </div>
        )}
        <div style={{ padding: '0 40px' }}><p className="err-note">{mode === 'view' ? err : ''}</p></div>
        <div className="drawer-foot">
          {mode === 'receive' ? (
            <>
              <button className="ghost-btn" onClick={() => { setMode('view'); setErr(''); }}>← ยกเลิก</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span className="txt">ยืนยันรับของ</span>
                <button className="arrow" onClick={confirmReceive} disabled={busy} aria-label="ยืนยันรับของ">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="#0A0A0A" strokeWidth="1.7"/></svg>
                </button>
              </div>
            </>
          ) : mode === 'editlines' ? (
            <>
              <button className="ghost-btn" onClick={() => { setMode('view'); setPicker(null); setErr(''); }}>← ยกเลิก</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <span className="txt">บันทึกรายการ</span>
                <button className="arrow" onClick={confirmEditLines} disabled={busy} aria-label="บันทึกรายการ">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
                </button>
              </div>
            </>
          ) : (
            <>
              <button className="ghost-btn" onClick={() => onPreview('delivery')} title="เลือกใบส่งของ / ใบสั่งซื้อ แล้วพิมพ์ได้ในนั้น">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" stroke="currentColor" strokeWidth="1.5"/><circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.5"/></svg>
                ดูตัวอย่าง / พิมพ์
              </button>
              {canWrite ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span className="txt" style={{ color: dirty ? 'var(--ink)' : 'var(--g3)' }}>บันทึกการแก้ไข</span>
                  <button className="arrow" onClick={save} disabled={busy || !dirty} aria-label="บันทึก">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
                  </button>
                </div>
              ) : (
                <span className="txt" style={{ color: 'var(--g3)' }}>ดูอย่างเดียว</span>
              )}
            </>
          )}
        </div>
      </aside>
      {picker && <ProductPicker onClose={() => setPicker(null)} onPick={p => pickProduct(picker, p)} />}
      {createFor && (
        // สร้างสินค้าจริงมาผูกแทนของขยะจากการ import (SKU N/A) — prefill ชื่อจากบรรทัด
        // ไม่ส่ง supplier เพื่อไม่ให้ upsert supplier_products ด้วยรหัสขยะ; save แล้วผูกเข้าบรรทัดทันที
        <ProductDrawer
          line={{ code: '', desc: editRows.find(x => x.key === createFor)?.product_name_snapshot ?? '' }}
          onClose={() => setCreateFor(null)}
          onSaved={p => { pickProduct(createFor, p); setCreateFor(null); }}
        />
      )}
    </>
  );
}

// (ProductPicker ย้ายไปเป็น component กลาง — src/components/ProductPicker.jsx ใช้ร่วมกับ Import PI)

// Printable PO document (browser print → paper/PDF)
function PoPrintSheet({ po, delivery, img }) {
  const sym = CUR_SYM[po.currency] ?? '';
  const items = po.purchase_order_items ?? [];
  const a = poAmounts(po);
  const bank = po.supplier?.contact?.bank ?? {};
  const foreignTotal = items.reduce((t, i) => t + Number(i.quantity || 0) * Number(i.unit_price_foreign || 0), 0);
  const thbTotal = items.reduce((t, i) => t + Number(i.quantity || 0) * Number(i.provisional_unit_cost || 0), 0);
  return (
    <div className="print-sheet">
      <div className="ps-top">
        <div>
          <img src="/camp-studio-logo.png" alt="CAMP STUDIO" style={{ height: 40 }} />
          <div className="ps-co">CAMP STUDIO (THAILAND) CO., LTD.</div>
          {/* เอกสารภายใน (ใบส่งของ) ไม่ใส่เบอร์โทร/เลขผู้เสียภาษี — ใส่เฉพาะใบ PO ที่ส่งซัพพลายเออร์ */}
          <div className="ps-sub">412 Sukhumvit 71, Phrakanong Nuea, Wattana, Bangkok 10110{!delivery && <><br />Tel. +66(0)20962566 · Tax ID 0105560181829</>}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="ps-title">{delivery ? 'ใบส่งของ · DELIVERY NOTE' : 'ใบสั่งซื้อ · PURCHASE ORDER'}</div>
          <div className="ps-no mono">{po.po_no}</div>
          {po.reference && <div className="ps-sub mono">PI Ref: {po.reference}</div>}
          <div className="ps-sub">วันที่ {fmtDate(po.created_at)}{po.expected_at ? ` · ETA ${fmtDate(po.expected_at)}` : ''}</div>
        </div>
      </div>

      <div className="ps-grid">
        <div>
          <div className="ps-h">ซัพพลายเออร์ · SUPPLIER</div>
          <div className="ps-co">{po.supplier?.company_name ?? po.brand_name ?? '—'}</div>
          {po.supplier?.contact?.address && <div className="ps-sub">{po.supplier.contact.address}</div>}
          {(po.supplier?.contact?.email || po.supplier?.contact?.phone) &&
            <div className="ps-sub">{[po.supplier?.contact?.email, po.supplier?.contact?.phone].filter(Boolean).join(' · ')}</div>}
        </div>
        {delivery ? (
        <div>
          <div className="ps-h">รับเข้าคลัง · RECEIVING</div>
          <div className="ps-sub">
            ตรวจนับตามรายการ แล้วเซ็นรับด้านล่าง<br />
            ของไม่ครบ/ชำรุด — เขียนกำกับในช่อง “รับจริง”
          </div>
        </div>
        ) : (
        <div>
          <div className="ps-h">การชำระเงิน · PAYMENT</div>
          <div className="ps-sub">
            {bank.bank_name && <>Bank: {bank.bank_name}<br /></>}
            {bank.account_no && <>Account: <span className="mono">{bank.account_no}</span><br /></>}
            {bank.swift && <>SWIFT: <span className="mono">{bank.swift}</span><br /></>}
            {bank.beneficiary && <>Beneficiary: {bank.beneficiary}<br /></>}
            {bank.bank_address && <>Bank address: {bank.bank_address}<br /></>}
            {po.currency && <>Currency: {po.currency}{po.fx_rate ? ` (FX → THB ${po.fx_rate})` : ''}</>}
          </div>
        </div>
        )}
      </div>

      <table className="ps-table">
        <thead>
          {delivery
            ? <tr><th>#</th><th className="ps-imgcol">รูป</th><th>SKU</th><th>รายการ · Description</th><th className="r">จำนวนที่สั่ง</th><th className="r" style={{ width: 110 }}>รับจริง</th></tr>
            : <tr><th>#</th><th className="ps-imgcol">รูป</th><th>SKU</th><th>รายการ · Description</th><th>รหัสซัพพลายเออร์</th>
                  <th className="r">จำนวน</th><th className="r">ราคา/หน่วย {sym}</th><th className="r">รวม {sym}</th></tr>}
        </thead>
        <tbody>
          {items.map((i, n) => (delivery ? (
            <tr key={i.id}>
              <td>{n + 1}</td>
              <td className="ps-imgcol">{i.line_type === 'product' ? psImg(img, i.product?.sku || i.sku_snapshot) : null}</td>
              <td className="mono">{i.sku_snapshot ?? '—'}</td>
              <td>{i.product_name_snapshot}{i.fee_category === 'claim' && ' · เครม'}</td>
              <td className="r mono">{fmt(i.quantity)}</td>
              <td className="ps-blank" />
            </tr>
          ) : (
            <tr key={i.id}>
              <td>{n + 1}</td>
              <td className="ps-imgcol">{i.line_type === 'product' ? psImg(img, i.product?.sku || i.sku_snapshot) : null}</td>
              <td className="mono">{i.sku_snapshot ?? '—'}</td>
              <td>{i.product_name_snapshot}{i.fee_category === 'claim' && ' · เครม (ฟรี)'}</td>
              <td className="mono">{i.supplier_product_code ?? '—'}</td>
              <td className="r mono">{fmt(i.quantity)}</td>
              <td className="r mono">{i.fee_category === 'claim' ? '0.00' : i.unit_price_foreign ? fmt(i.unit_price_foreign, 2) : fmt(i.unit_price, 2)}</td>
              <td className="r mono">{i.fee_category === 'claim' ? '0.00' : i.unit_price_foreign ? fmt(i.quantity * i.unit_price_foreign, 2) : fmt(i.quantity * i.unit_price, 2)}</td>
            </tr>
          )))}
        </tbody>
        <tfoot>
          {delivery ? (
            <tr><td colSpan="4">รวมทั้งสิ้น · TOTAL</td><td className="r mono">{fmt(a.qty)}</td><td className="ps-blank" /></tr>
          ) : (<>
            <tr>
              <td colSpan="5">รวมทั้งสิ้น · TOTAL</td>
              <td className="r mono">{fmt(a.qty)}</td>
              <td></td>
              <td className="r mono">{a.isForeign ? `${sym}${fmt(foreignTotal, 2)}` : `฿${fmt(po.total_amount, 2)}`}</td>
            </tr>
            {a.isForeign && thbTotal > 0 && (
              <tr className="ps-thb"><td colSpan="7">≈ มูลค่าโดยประมาณ (THB)</td><td className="r mono">฿{fmt(thbTotal, 2)}</td></tr>
            )}
          </>)}
        </tfoot>
      </table>

      <div className="ps-sign">
        <div><div className="ps-line" /><div className="ps-sub">{delivery ? 'ผู้ส่งของ · Delivered by' : 'ผู้สั่งซื้อ · Ordered by'}</div></div>
        <div><div className="ps-line" /><div className="ps-sub">{delivery ? 'ผู้รับของ · Received by' : 'ผู้อนุมัติ · Approved by'}</div></div>
      </div>
    </div>
  );
}

/* ---------- ใบซื้อด่วน (QUICK BUY / iPhone) — กางดูรายการ + พิมพ์ ----------
   เอกสารคนละชนิดกับใบสั่งซื้อ: บาทล้วน ไม่มีเลข PI / สกุลเงิน / ค่าขนส่งเฉลี่ย
   แก้ไขทำที่หน้า QUICK BUY ที่เดียว (ปุ่มด้านล่างพาไป) จะได้ไม่มีสองที่แก้ */
// รูปในใบพิมพ์: ใช้ <img> เท่านั้น (background-image ไม่ถูกโหลดตอนอยู่ใน display:none
// และ Safari ปิด "Print backgrounds" เป็นค่าเริ่มต้น) · ไม่มีรูป = ตีกรอบไว้ เพราะสีพื้นไม่ติดกระดาษ
function psImg(map, sku) {
  const url = sku && map ? map.get(sku) : null;
  if (!url) return <span className="ps-noimg" />;
  return <img className="ps-img" src={url} alt="" loading="eager" onError={e => { e.target.style.visibility = 'hidden'; }} />;
}
const qbQty = qb => (qb.lines ?? []).reduce((t, l) => t + (Number(l.qty) || 0), 0);
const qbWho = a => { const s = String(a || '').replace(/^buy:/, '').split('@')[0]; return s || '—'; };
const qbBin = qb => `${qb.bin || 'Free zone'}${qb.warehouse_code === 'W0001' ? ' · สาขา 71' : qb.warehouse_code ? ` · ${qb.warehouse_code}` : ''}`;

function QbDrawer({ qb, onClose, onPreview, onChanged }) {
  const [mode, setMode] = useState('view');          // 'view' | 'edit'
  const [lines, setLines] = useState([]);
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [eta, setEta] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const deb = useRef(null);
  const searchSeq = useRef(0);
  const searchRef = useRef(null);
  const [scanNote, setScanNote] = useState('');

  const chip = qb.status === 'received' ? ['รับครบ', 'ok'] : ['กำลังมา', 'amb']; // คำเดียวกับใบ PO
  const canEdit = qb.status === 'incoming';   // รับของแล้ว/ยกเลิกแล้ว ห้ามแตะ เลขสต๊อกออกไปแล้ว

  function startEdit() {
    setLines((qb.lines ?? []).map(l => ({
      sku: l.sku, name: l.name || l.sku,
      qty: String(l.qty ?? ''), unit_cost_thb: String(l.unit_cost_thb ?? ''),
    })));
    setSupplier(qb.supplier || ''); setNote(qb.note || ''); setEta(qb.expected_at || '');
    setQ(''); setRows(null); setErr(''); setMode('edit');
  }
  const setLine = (i, patch) => setLines(ls => ls.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  function search(v) {
    setQ(v);
    clearTimeout(deb.current);
    const t = cleanText(v).trim();
    if (t.length < 2) { setRows(null); setSearching(false); return; }
    setSearching(true);
    // ปืนบาร์โค้ดไม่กด Enter ให้ (Purim 3 ต.ค. 69: ยิงรอบสองไปต่อท้ายรอบแรก) — รหัสตัวเลขล้วนรอสั้นลง
    // แล้วถ้าเจอสินค้าตัวเดียวตรงบาร์โค้ด/SKU = +1 ให้เอง ล้างช่อง คืนโฟกัส ยิงตัวถัดไปได้ทันที
    deb.current = setTimeout(() => runSearch(t), /^[0-9]{6,}$/.test(t) ? 180 : 350);
  }
  async function runSearch(t) {
    const seq = ++searchSeq.current;   // กันผลเก่าที่มาช้ากว่ามาทับ / บวกซ้ำ
    try {
      const isCode = /^[0-9]{6,}$/.test(t);
      const o = await authFetch(`/api/pd/search?${isCode ? `barcode=${encodeURIComponent(t)}` : `q=${encodeURIComponent(t)}`}`, { method: 'GET' });
      if (seq !== searchSeq.current) return;
      const found = o.rows || [];
      if (isCode && found.length === 1) {
        addLine(found[0]);
        setScanNote(`✓ เพิ่ม ${found[0].name || found[0].sku} +1`);
        setTimeout(() => searchRef.current?.focus(), 0);
        return;
      }
      setRows(found);
    } catch (e) { if (seq === searchSeq.current) { setRows([]); setErr(e.message); } }
    finally { if (seq === searchSeq.current) setSearching(false); }
  }
  // ปืนบางรุ่นส่ง Enter/Tab ท้ายรหัส — ค้นทันที ไม่ต้องรอ
  function onSearchKey(e) {
    if (e.key !== 'Enter' && e.key !== 'Tab') return;
    const t = cleanText(q).trim();
    if (t.length < 2) return;
    e.preventDefault();
    clearTimeout(deb.current);
    if (rows?.length === 1) { searchSeq.current++; addLine(rows[0]); setScanNote(`✓ เพิ่ม ${rows[0].name || rows[0].sku} +1`); setSearching(false); return; }
    setSearching(true);
    runSearch(t);
  }
  function addLine(p) {
    setLines(ls => {
      const i = ls.findIndex(x => x.sku === p.sku);
      if (i >= 0) return ls.map((x, k) => (k === i ? { ...x, qty: String((Number(x.qty) || 0) + 1) } : x));
      return [...ls, { sku: p.sku, name: p.name, qty: '1', unit_cost_thb: p.purchase_cost != null ? String(p.purchase_cost) : '' }];
    });
    setQ(''); setRows(null);
  }

  async function save() {
    if (busy) return;
    const out = lines
      .map(l => ({ sku: l.sku, name: l.name, qty: Number(l.qty) || 0, unit_cost_thb: parseFloat(l.unit_cost_thb) || 0 }))
      .filter(l => l.sku && l.qty > 0);
    if (!out.length) { setErr('ต้องเหลืออย่างน้อย 1 รายการ — ถ้าจะทิ้งทั้งใบให้กดยกเลิกใบแทน'); return; }
    setBusy(true); setErr('');
    try {
      await authFetch('/api/pd/quick', { method: 'POST', body: JSON.stringify({ op: 'update', id: qb.id, lines: out, supplier, note, expected_at: eta }) });
      setMode('view');
      await onChanged(false);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function receive() {
    if (busy) return;
    if (!window.confirm('รับของเข้าใบนี้?\n\nจำนวนจะถูกยิงเข้าสต๊อกจริงที่ Free zone สาขา 71 ทันที และสินค้าพร้อมขายใน PD จะขึ้นตาม')) return;
    setBusy(true); setErr('');
    try {
      await authFetch('/api/pd/quick', { method: 'POST', body: JSON.stringify({ op: 'receive', id: qb.id }) });
      await onChanged(false);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function cancel() {
    if (busy) return;
    if (!window.confirm('ยกเลิกใบซื้อนี้? (ใบที่ยังไม่รับของเท่านั้น)')) return;
    setBusy(true); setErr('');
    try {
      await authFetch('/api/pd/quick', { method: 'POST', body: JSON.stringify({ op: 'cancel', id: qb.id }) });
      await onChanged(true);
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  // กดรับไปแล้วเจอจำนวน/ของผิด (Purim 1 ส.ค.): ถอนสต๊อกคืน → ใบกลับ "กำลังมา" → แก้ → กดรับใหม่
  // (คู่ขนานปุ่มยกเลิกใบรับของฝั่ง PO — รับรอบใหม่ pd-api ออก doc_ref ใหม่เอง กัน fn_stock_in ข้ามซ้ำ)
  async function unreceive() {
    if (busy) return;
    if (!window.confirm('ยกเลิกรับของใบนี้?\n\n• ของที่รับเข้าคลังจากใบนี้จะถูกถอนออก (สต๊อกลดกลับ)\n• ใบกลับมาสถานะ "กำลังมา" — แก้จำนวน/รายการได้ตามปกติ\n• แก้เสร็จแล้วกด "รับของเข้าคลัง" ใหม่ได้เลย')) return;
    setBusy(true); setErr('');
    try {
      await authFetch('/api/pd/quick', { method: 'POST', body: JSON.stringify({ op: 'unreceive', id: qb.id }) });
      await onChanged(false);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  // ---------- เอกสารแนบ (PI / ใบเสร็จ / รูป) — Purim 7 ก.ย.: "อยากแนบ PI [PDF] เข้าไปในรายการนี้" ----------
  // ไฟล์ขึ้น bucket sourcepoint-docs ด้วย JWT ผู้ใช้ (policy: role ต้นทุน อ่าน/อัปโหลด) → metadata เข้าใบผ่าน /api/pd/quick
  const attInput = useRef(null);
  const [attBusy, setAttBusy] = useState(false);
  const attachments = Array.isArray(qb.attachments) ? qb.attachments : [];
  async function attachFiles(files) {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length || attBusy) return;
    setAttBusy(true); setErr('');
    try {
      for (const f of list) {
        if (f.size > 30 * 1024 * 1024) throw new Error(`${f.name}: ไฟล์ใหญ่เกิน 30MB`);
        const safe = f.name.replace(/[^A-Za-z0-9._-]/g, '_').slice(-120);
        const path = `quick-buy/${qb.id}/${Date.now()}_${safe}`;
        const { error: upErr } = await supabase.storage.from('sourcepoint-docs')
          .upload(path, f, { contentType: f.type || undefined, upsert: false });
        if (upErr) throw new Error(`อัปโหลด ${f.name} ไม่สำเร็จ: ${upErr.message}`);
        await authFetch('/api/pd/quick', { method: 'POST', body: JSON.stringify({ op: 'attach', id: qb.id, path, name: f.name, size: f.size, mime: f.type || null }) });
      }
      await onChanged(false);
    } catch (e) { setErr(e.message); } finally { setAttBusy(false); if (attInput.current) attInput.current.value = ''; }
  }
  async function openAtt(a) {
    const { data, error } = await supabase.storage.from('sourcepoint-docs').createSignedUrl(a.path, 600);
    if (error || !data?.signedUrl) { setErr('เปิดไฟล์ไม่ได้ — ' + (error?.message || 'ตรวจสิทธิ์การเข้าถึง')); return; }
    window.open(data.signedUrl, '_blank');
  }
  async function downloadAtt(a) {
    const { data, error } = await supabase.storage.from('sourcepoint-docs').createSignedUrl(a.path, 600, { download: a.name });
    if (error || !data?.signedUrl) { setErr('ดาวน์โหลดไม่ได้ — ' + (error?.message || 'ตรวจสิทธิ์การเข้าถึง')); return; }
    const el = document.createElement('a'); el.href = data.signedUrl; el.download = a.name; document.body.appendChild(el); el.click(); el.remove();
  }
  async function removeAtt(a) {
    if (attBusy) return;
    if (!window.confirm(`ลบไฟล์ "${a.name}" ออกจากใบนี้?\n\nไฟล์จะถูกลบออกจากระบบด้วย`)) return;
    setAttBusy(true); setErr('');
    try {
      await authFetch('/api/pd/quick', { method: 'POST', body: JSON.stringify({ op: 'detach', id: qb.id, path: a.path }) });
      await onChanged(false);
    } catch (e) { setErr(e.message); } finally { setAttBusy(false); }
  }

  const editQty = lines.reduce((t, l) => t + (Number(l.qty) || 0), 0);
  const editThb = lines.reduce((t, l) => t + (Number(l.qty) || 0) * (parseFloat(l.unit_cost_thb) || 0), 0);
  const viewLines = qb.lines ?? [];

  return (
    <>
      <div className="scrim open" onClick={onClose} />
      <aside className="drawer open doc-center qb-wide" aria-hidden="false">
        <div className="drawer-head">
          <div>
            <div className="eyebrow">ใบซื้อด่วน · Quick buy</div>
            <h2 className="mono">{qb.receipt_no || 'ใบซื้อด่วน'}</h2>
            <div className="po-date" style={{ marginTop: 6 }}>
              <span className={`po-chip ${chip[1]}`}>{chip[0]}</span>
              <span style={{ marginLeft: 10 }}>{qb.supplier || 'ไม่ระบุที่มา'}</span>
            </div>
          </div>
          <button className="xbtn" onClick={onClose} aria-label="ปิด">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6" /></svg>
          </button>
        </div>

        <div className="drawer-body">
          {mode === 'view' ? (
            <>
              <div className="sd-sec">
                <div className="map-row"><span className="pi">สร้างเมื่อ</span><span className="sys">{fmtDate(qb.created_at)} · โดย {qbWho(qb.created_by)}</span></div>
                {qb.expected_at && <div className="map-row"><span className="pi">ของถึงคลัง</span><span className="sys"><b>{fmtDate(qb.expected_at)}</b></span></div>}
                {qb.status === 'received'
                  ? <div className="map-row"><span className="pi">รับของแล้ว</span><span className="sys">{fmtDate(qb.received_at)} · โดย {qbWho(qb.received_by)} · เข้า {qbBin(qb)}</span></div>
                  : <div className="map-row"><span className="pi">ปลายทาง</span><span className="sys">{qbBin(qb)} — ยังไม่ได้เพิ่มสต๊อกจริง</span></div>}
                {qb.note && <div className="map-row"><span className="pi">หมายเหตุ</span><span className="sys">{qb.note}</span></div>}
              </div>

              <div className="sd-sec">
                <h5>รายการ ({viewLines.length})</h5>
                <div className="qb-ehead"><span>สินค้า</span><span className="num">จำนวน</span><span className="num">ต้นทุน/ชิ้น ฿</span><span className="num">รวม</span><span /></div>
                {viewLines.map((l, i) => (
                  <div className="qb-eline" key={(l.sku || '') + i}>
                    <div style={{ minWidth: 0 }}>
                      <div className="qb-name">{l.name || l.sku}</div>
                      <div className="qb-sub mono">{l.sku}</div>
                    </div>
                    <div className="num mono">{fmt(l.qty)}</div>
                    <div className="num mono">{fmt(l.unit_cost_thb, 2)}</div>
                    <div className="qb-lt mono">฿{fmt((Number(l.qty) || 0) * (Number(l.unit_cost_thb) || 0), 2)}</div>
                    <span />
                  </div>
                ))}
                <div className="qb-total"><span>รวม {viewLines.length} รายการ · {fmt(qbQty(qb))} ชิ้น</span><b className="mono">฿{fmt(qb.total_thb, 2)}</b></div>
              </div>

              <div className="sd-sec qb-attsec">
                <div className="qb-att-head">
                  <h5>เอกสารแนบ · PI / ใบเสร็จ ({attachments.length})</h5>
                  <input ref={attInput} type="file" multiple accept=".pdf,image/*,.xlsx,.xls,.csv,.doc,.docx" style={{ display: 'none' }}
                         onChange={e => attachFiles(e.target.files)} />
                  <button className="ghost-btn qb-att-add" disabled={attBusy} onClick={() => attInput.current?.click()}>
                    {attBusy ? 'กำลังอัปโหลด…' : '+ แนบไฟล์ PI / ใบเสร็จ'}
                  </button>
                </div>
                {attachments.length === 0 ? (
                  <p className="sd-empty" style={{ margin: '6px 0 0' }}>ยังไม่มีไฟล์แนบ — PDF หรือรูปถ่ายใบ PI / ใบเสร็จ แนบได้หลายไฟล์ (ไม่เกิน 30MB/ไฟล์)</p>
                ) : attachments.map(a => (
                  <div className="qb-att" key={a.path}>
                    <span className="qb-att-ic" aria-hidden="true">{/^image\//.test(a.mime || '') ? '🖼' : /pdf/i.test(a.mime || a.name || '') ? '📄' : '📎'}</span>
                    <div style={{ minWidth: 0 }}>
                      <button className="qb-att-name" onClick={() => openAtt(a)} title="เปิดดู">{a.name}</button>
                      <div className="qb-sub">{[a.size ? fmtBytes(a.size) : '', a.at ? fmtDate(a.at) : '', a.by ? String(a.by).replace(/^buy:/, '') : ''].filter(Boolean).join(' · ')}</div>
                    </div>
                    <div className="qb-att-act">
                      <button className="qb-att-btn" onClick={() => openAtt(a)}>เปิด ↗</button>
                      <button className="qb-att-btn" onClick={() => downloadAtt(a)}>ดาวน์โหลด</button>
                      <button className="qb-att-btn danger" disabled={attBusy} onClick={() => removeAtt(a)}>ลบ</button>
                    </div>
                  </div>
                ))}
              </div>

              {canEdit && (
                <div className="qb-actions" style={{ justifyContent: 'flex-start', margin: '4px 0 18px' }}>
                  <button className="ghost-btn" onClick={startEdit}>แก้รายการ / ต้นทุน</button>
                  <button className="ghost-btn" onClick={cancel} disabled={busy}>ยกเลิกใบนี้</button>
                </div>
              )}
              {qb.status === 'received' && (
                <div className="qb-actions" style={{ justifyContent: 'flex-start', margin: '4px 0 18px' }}>
                  <button className="ghost-btn" onClick={unreceive} disabled={busy}>
                    ↩ ยกเลิกรับของ — ถอนสต๊อกคืน แล้วกลับมาแก้ใบได้
                  </button>
                </div>
              )}

              <div className="cost-note"><span>
                ใบซื้อด่วนเป็นเอกสารบาทล้วน — แนบไฟล์ PI/ใบเสร็จไว้อ้างอิงได้ แต่ไม่มีเลขที่ PI / สกุลเงินต่างประเทศ / ค่าขนส่งเฉลี่ยเข้าต้นทุน
                ถ้าต้องการให้ตัวเลขพวกนี้เข้าต้นทุน ให้ทำเป็น <b>ใบสั่งซื้อ</b> จากหน้า “by PI” แทน
              </span></div>
            </>
          ) : (
            <>
              <div className="qb-ehead"><span>สินค้าในใบนี้</span><span className="num">จำนวน</span><span className="num">ต้นทุน/ชิ้น ฿</span><span className="num">รวม</span><span /></div>
              {lines.map((l, i) => (
                <div className="qb-eline" key={l.sku + i}>
                  <div style={{ minWidth: 0 }}><div className="qb-name">{l.name || l.sku}</div><div className="qb-sub mono">{l.sku}</div></div>
                  <input className="under mono qb-in" inputMode="numeric" value={l.qty}
                         onChange={e => setLine(i, { qty: e.target.value.replace(/[^0-9.]/g, '') })} />
                  <input className="under mono qb-in" inputMode="decimal" value={l.unit_cost_thb}
                         onChange={e => setLine(i, { unit_cost_thb: e.target.value.replace(/[^0-9.]/g, '') })} />
                  <div className="qb-lt mono">฿{fmt((Number(l.qty) || 0) * (parseFloat(l.unit_cost_thb) || 0), 2)}</div>
                  <button className="qb-rm" onClick={() => setLines(ls => ls.filter((_, k) => k !== i))} aria-label="ลบรายการ">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" /></svg>
                  </button>
                </div>
              ))}

              <div className="prod-search qb-esearch">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="#0A0A0A" strokeWidth="1.6" /><path d="M16.5 16.5L21 21" stroke="#0A0A0A" strokeWidth="1.6" /></svg>
                <input ref={searchRef} value={q} placeholder="เพิ่มสินค้าเข้าใบนี้ — ยิงบาร์โค้ด / พิมพ์ SKU / ชื่อ"
                       onChange={e => { setScanNote(''); search(e.target.value); }} onKeyDown={onSearchKey} />
                {searching && <span className="sd-empty" style={{ margin: 0, whiteSpace: 'nowrap' }}>ค้น…</span>}
              </div>
              {scanNote && !q && <p className="qb-scan-note">{scanNote} — ยิงตัวถัดไปได้เลย</p>}
              {rows?.length > 0 && (
                <div className="qb-results" style={{ marginBottom: 14 }}>
                  {rows.map(p => (
                    <div className="qb-res" key={p.sku} onClick={() => addLine(p)}>
                      {p.image_url ? <img src={IMG(p.image_url)} alt="" onError={e => { e.target.style.opacity = .15; }} /> : <div className="qb-noimg" />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="qb-name">{p.name}</div>
                        <div className="qb-sub mono">{p.sku}</div>
                      </div>
                      <div className="qb-add">+ เพิ่ม</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="field">
                <div className="lbl"><span className="th">ซัพพลายเออร์ / ที่มา</span></div>
                <input className="under" value={supplier} onChange={e => setSupplier(e.target.value)} />
              </div>
              <div className="field">
                <div className="lbl"><span className="th">วันที่ของจะถึงคลัง</span><span className="opt">ไม่บังคับ</span></div>
                <input className="under mono" type="date" value={eta} onChange={e => setEta(e.target.value)} />
              </div>
              <div className="field">
                <div className="lbl"><span className="th">หมายเหตุ</span></div>
                <input className="under" value={note} onChange={e => setNote(e.target.value)} />
              </div>
              <p className="sd-empty">รวม {lines.length} รายการ · {fmt(editQty)} ชิ้น · ฿{fmt(editThb, 2)}</p>
            </>
          )}
          <p className="err-note">{err}</p>
        </div>

        <div className="drawer-foot">
          {mode === 'edit' ? (
            <>
              <button className="ghost-btn" onClick={() => { setMode('view'); setErr(''); }}>← ยกเลิกการแก้</button>
              <button className="add-sup" style={{ padding: '12px 20px', fontSize: 13 }} onClick={save} disabled={busy || !lines.length}>
                {busy ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
              </button>
            </>
          ) : (
            <>
              <button className="ghost-btn" onClick={() => onPreview('delivery')} title="เลือกใบส่งของ / ใบซื้อ แล้วพิมพ์ได้ในนั้น">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" stroke="currentColor" strokeWidth="1.5"/><circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.5"/></svg>
                ดูตัวอย่าง / พิมพ์
              </button>
              {canEdit && (
                <button className="add-sup" style={{ padding: '12px 20px', fontSize: 13 }} onClick={receive} disabled={busy}>
                  {busy ? 'กำลังรับ…' : 'รับของเข้าคลัง'}
                </button>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function QbPrintSheet({ qb, delivery, img }) {
  const lines = qb.lines ?? [];
  return (
    <div className="print-sheet">
      <div className="ps-top">
        <div>
          <img src="/camp-studio-logo.png" alt="CAMP STUDIO" style={{ height: 40 }} />
          <div className="ps-co">CAMP STUDIO (THAILAND) CO., LTD.</div>
          {/* ใบซื้อด่วน/ใบส่งของ = เอกสารภายในทั้งคู่ — ไม่ใส่เบอร์โทร/เลขผู้เสียภาษี */}
          <div className="ps-sub">412 Sukhumvit 71, Phrakanong Nuea, Wattana, Bangkok 10110</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="ps-title">{delivery ? 'ใบส่งของ · DELIVERY NOTE' : 'ใบซื้อด่วน · QUICK BUY'}</div>
          <div className="ps-no mono">{qb.receipt_no || '—'}</div>
          <div className="ps-sub">วันที่ {fmtDate(qb.created_at)}{qb.expected_at ? ` · ของถึง ${fmtDate(qb.expected_at)}` : ''}</div>
        </div>
      </div>

      <div className="ps-grid">
        <div>
          <div className="ps-h">ซัพพลายเออร์ · SUPPLIER</div>
          <div className="ps-co">{qb.supplier || '—'}</div>
          {qb.note && <div className="ps-sub">{qb.note}</div>}
        </div>
        <div>
          <div className="ps-h">รับเข้าคลัง · RECEIVING</div>
          <div className="ps-sub">
            ปลายทาง: {qbBin(qb)}<br />
            สถานะ: {qb.status === 'received' ? `รับของแล้ว ${fmtDate(qb.received_at)}` : 'รอรับเข้าคลัง'}
            {delivery ? <><br />ตรวจนับตามรายการ แล้วเซ็นรับด้านล่าง</> : <><br />สกุลเงิน: THB</>}
          </div>
        </div>
      </div>

      <table className="ps-table">
        <thead>
          {delivery
            ? <tr><th>#</th><th className="ps-imgcol">รูป</th><th>SKU</th><th>รายการ · Description</th><th className="r">จำนวน</th><th className="r" style={{ width: 110 }}>รับจริง</th></tr>
            : <tr><th>#</th><th className="ps-imgcol">รูป</th><th>SKU</th><th>รายการ · Description</th>
                  <th className="r">จำนวน</th><th className="r">ต้นทุน/หน่วย ฿</th><th className="r">รวม ฿</th></tr>}
        </thead>
        <tbody>
          {lines.map((l, n) => (
            <tr key={(l.sku || '') + n}>
              <td>{n + 1}</td>
              <td className="ps-imgcol">{psImg(img, l.sku)}</td>
              <td className="mono">{l.sku ?? '—'}</td>
              <td>{l.name}</td>
              <td className="r mono">{fmt(l.qty)}</td>
              {delivery ? <td className="ps-blank" /> : <>
                <td className="r mono">{fmt(l.unit_cost_thb, 2)}</td>
                <td className="r mono">{fmt((Number(l.qty) || 0) * (Number(l.unit_cost_thb) || 0), 2)}</td>
              </>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {delivery ? (
            <tr><td colSpan="4">รวมทั้งสิ้น · TOTAL</td><td className="r mono">{fmt(qbQty(qb))}</td><td className="ps-blank" /></tr>
          ) : (
            <tr>
              <td colSpan="4">รวมทั้งสิ้น · TOTAL</td>
              <td className="r mono">{fmt(qbQty(qb))}</td>
              <td></td>
              <td className="r mono">฿{fmt(qb.total_thb, 2)}</td>
            </tr>
          )}
        </tfoot>
      </table>

      <div className="ps-sign">
        <div><div className="ps-line" /><div className="ps-sub">{delivery ? 'ผู้ส่งของ · Delivered by' : 'ผู้ซื้อ · Bought by'}</div></div>
        <div><div className="ps-line" /><div className="ps-sub">ผู้รับของ · Received by</div></div>
      </div>
    </div>
  );
}

/* ---------- สร้างใบซื้อด่วน / ทำร่างจากมือถือต่อ ----------
   ย้ายมาจากหน้า QUICK BUY เดิม (Purim: เมนูซ้ำกับ iPhone) — งานทั้งหมดของใบซื้อด่วน
   อยู่ในหน้า "ใบสั่งซื้อ" ที่เดียว: สร้าง → แก้ → รับของ → พิมพ์ */
function QbNewDrawer({ draft, onClose, onDone, onDraftSaved }) {
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [eta, setEta] = useState('');
  const [cart, setCart] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [makeProduct, setMakeProduct] = useState(null);
  const [matchUnknown, setMatchUnknown] = useState(null); // จับคู่ตัวยิงแล้ว PD ไม่รู้จักบาร์โค้ด → สินค้าเดิม
  const [supList, setSupList] = useState([]);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [draftId, setDraftId] = useState(null);
  const deb = useRef(null);
  const searchSeq = useRef(0);
  const searchRef = useRef(null);
  const [scanNote, setScanNote] = useState('');
  const baseRef = useRef(null);      // payload ดิบของร่าง — เก็บคีย์ที่หน้านี้ไม่ได้ถือไว้

  useEffect(() => {
    supabase.from('suppliers').select('id, company_name').eq('is_active', true).order('company_name')
      .then(({ data }) => setSupList(data || []));
  }, []);
  useEffect(() => {
    if (!draft) return;
    const pl = draft.payload || {};
    baseRef.current = pl;
    setDraftId(draft.id);
    setSupplier(pl.supplier || ''); setNote(pl.note || ''); setEta(pl.expected_at || '');
    setCart(Array.isArray(pl.cart) ? pl.cart : []);
    setUnknown(Array.isArray(pl.unknown) ? pl.unknown : []);
    // ร่างเก่าที่บางบรรทัดยังไม่มีทุน (เช่นตัวที่จับคู่ไว้ก่อนมีท่อนี้) — ดึงราคาซื้อล่าสุดจาก PD มาเติมให้
    const need = (Array.isArray(pl.cart) ? pl.cart : [])
      .filter(l => l.sku && !(parseFloat(l.unit_cost) > 0)).map(l => l.sku);
    if (need.length) {
      authFetch('/api/pd/costs', { method: 'POST', body: JSON.stringify({ skus: need }) })
        .then(o => {
          const rows = o?.rows || {};
          setCart(c => c.map(l => {
            const r = rows[l.sku];
            if (!r || r.purchase_cost == null || parseFloat(l.unit_cost) > 0) return l;
            return { ...l, purchase_cost: r.purchase_cost, unit_cost: String(r.purchase_cost) };
          }));
        })
        .catch(e => console.warn('ดึงราคาซื้อล่าสุดจาก PD ไม่สำเร็จ:', e?.message ?? e));
    }
  }, [draft]);

  // เติมต้นทุนจาก "ราคาซื้อล่าสุด" ใน PD ให้บรรทัดที่เพิ่งจับคู่ (ค้นด้วยชื่อ/ยิงบาร์โค้ดได้ทุนติดมาอยู่แล้ว
  // แต่ตัวที่จับคู่จากรายการยังไม่รู้จัก ไม่มีทุนติดมา — Purim 4 ส.ค.: "ราคาต้นทุนไม่มา")
  // labelPrice = ราคาบนป้ายที่ถ่าย/พิมพ์จากมือถือ = ราคาขาย ไม่ใช่ทุน → ส่งเข้า PD เป็น default_price ถ้ายังว่าง
  async function fillCostFromPd(sku, labelPrice) {
    try {
      const o = await authFetch('/api/pd/costs', { method: 'POST', body: JSON.stringify({ skus: [sku] }) });
      const r = o?.rows?.[sku];
      if (r) {
        setCart(c => c.map(x => (x.sku === sku
          ? { ...x, purchase_cost: r.purchase_cost ?? x.purchase_cost,
              unit_cost: (!x.unit_cost && r.purchase_cost != null) ? String(r.purchase_cost) : x.unit_cost }
          : x)));
      }
      const price = Number(String(labelPrice ?? '').replace(/[^\d.]/g, ''));
      if (price > 0 && !(r && Number(r.default_price) > 0)) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await fetch('/api/pd/create-product', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ sku, name: r?.name_en || r?.name_th || sku, default_price: price, patch_only: true }),
          });
        }
      }
    } catch (e) { console.warn('ดึงราคาซื้อล่าสุดจาก PD ไม่สำเร็จ (กรอกทุนเองได้):', e?.message ?? e); }
  }

  const supExists = () => supList.some(x => (x.company_name || '').toLowerCase() === supplier.trim().toLowerCase());
  async function ensureSupplier() {
    const name = supplier.trim();
    if (!name) return null;
    const hit = supList.find(x => (x.company_name || '').toLowerCase() === name.toLowerCase());
    if (hit) return hit.company_name;
    const { data, error } = await supabase.from('suppliers').insert({ company_name: name }).select('id, company_name').single();
    if (!error && data) { setSupList(l => [...l, data]); return data.company_name; }
    return name;
  }

  function search(v) {
    setQ(v);
    clearTimeout(deb.current);
    const t = cleanText(v).trim();
    if (t.length < 2) { setRows(null); setSearching(false); return; }
    setSearching(true);
    // ปืนบาร์โค้ดไม่กด Enter ให้ (Purim 3 ต.ค. 69: ยิงรอบสองไปต่อท้ายรอบแรก) — รหัสตัวเลขล้วนรอสั้นลง
    // แล้วถ้าเจอสินค้าตัวเดียวตรงบาร์โค้ด/SKU = +1 ให้เอง ล้างช่อง คืนโฟกัส ยิงตัวถัดไปได้ทันที
    deb.current = setTimeout(() => runSearch(t), /^[0-9]{6,}$/.test(t) ? 180 : 350);
  }
  async function runSearch(t) {
    const seq = ++searchSeq.current;   // กันผลเก่าที่มาช้ากว่ามาทับ / บวกซ้ำ
    try {
      const isCode = /^[0-9]{6,}$/.test(t);
      const o = await authFetch(`/api/pd/search?${isCode ? `barcode=${encodeURIComponent(t)}` : `q=${encodeURIComponent(t)}`}`, { method: 'GET' });
      if (seq !== searchSeq.current) return;
      const found = o.rows || [];
      if (isCode && found.length === 1) {
        addToCart(found[0]);
        setScanNote(`✓ เพิ่ม ${found[0].name || found[0].sku} +1`);
        setTimeout(() => searchRef.current?.focus(), 0);
        return;
      }
      setRows(found);
    } catch (e) { if (seq === searchSeq.current) { setRows([]); setErr(e.message); } }
    finally { if (seq === searchSeq.current) setSearching(false); }
  }
  // ปืนบางรุ่นส่ง Enter/Tab ท้ายรหัส — ค้นทันที ไม่ต้องรอ
  function onSearchKey(e) {
    if (e.key !== 'Enter' && e.key !== 'Tab') return;
    const t = cleanText(q).trim();
    if (t.length < 2) return;
    e.preventDefault();
    clearTimeout(deb.current);
    if (rows?.length === 1) { searchSeq.current++; addToCart(rows[0]); setScanNote(`✓ เพิ่ม ${rows[0].name || rows[0].sku} +1`); setSearching(false); return; }
    setSearching(true);
    runSearch(t);
  }
  function addToCart(p) {
    setCart(c => {
      const i = c.findIndex(x => x.sku === p.sku);
      if (i >= 0) return c.map((x, k) => (k === i ? { ...x, qty: Number(x.qty || 0) + 1 } : x));
      return [...c, { sku: p.sku, name: p.name, barcode: p.barcode, image_url: p.image_url, purchase_cost: p.purchase_cost,
                      on_hand: p.on_hand, qty: 1, unit_cost: p.purchase_cost != null ? String(p.purchase_cost) : '' }];
    });
    setQ(''); setRows(null);
  }
  const setLine = (sku, patch) => setCart(c => c.map(x => (x.sku === sku ? { ...x, ...patch } : x)));
  const totalQty = cart.reduce((t, l) => t + (Number(l.qty) || 0), 0);
  const totalThb = cart.reduce((t, l) => t + (Number(l.qty) || 0) * (parseFloat(l.unit_cost) || 0), 0);

  const payload = () => ({ ...(baseRef.current || {}), supplier, note, expected_at: eta, cart, unknown });
  async function saveDraft() {
    if (busy) return;
    if (!cart.length && !unknown.length) { setErr('ยังไม่มีรายการ'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const head = cart[0]?.name || (unknown[0] ? `บาร์โค้ด ${unknown[0].barcode}` : '');
      const more = cart.length + unknown.length - 1;
      const title = `${head}${more > 0 ? ` +${more}` : ''}${unknown.length ? ` · ไม่รู้จัก ${unknown.length}` : ''}`.slice(0, 120);
      const o = await authFetch('/api/pd/drafts', { method: 'POST', body: JSON.stringify({ id: draftId || undefined, title, payload: payload() }) });
      setDraftId(o.id);
      setMsg('บันทึกร่างแล้ว');
      onDraftSaved?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function createBuy() {
    if (busy) return;
    const lines = cart.map(l => ({ sku: l.sku, name: l.name, qty: Number(l.qty) || 0, unit_cost_thb: parseFloat(l.unit_cost) || 0 }))
      .filter(l => l.sku && l.qty > 0);
    if (!lines.length) { setErr('ใส่จำนวนอย่างน้อย 1 รายการ'); return; }
    if (unknown.length) { setErr(`ยังมีสินค้าที่ยังไม่รู้จัก ${unknown.length} ตัว — กด “สร้างสินค้า” ให้ครบก่อน หรือลบทิ้ง`); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const supName = await ensureSupplier();
      await authFetch('/api/pd/quick', { method: 'POST', body: JSON.stringify({ op: 'create', lines, supplier: supName, note, expected_at: eta || undefined }) });
      if (draftId) {
        try { await authFetch('/api/pd/drafts', { method: 'DELETE', body: JSON.stringify({ id: draftId }) }); } catch { /* ร่างค้างไว้ไม่เป็นไร */ }
      }
      await onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <>
      <div className="scrim open" onClick={onClose} />
      {/* กางใหญ่กลางจอ — ลิ้นชักแคบทำให้ชื่อสินค้ายาวถูกตัด ทำงานยาก (Purim 7 ก.ย.) */}
      <aside className="drawer open doc-center qb-wide" aria-hidden="false">
        <div className="drawer-head">
          <div>
            <div className="eyebrow">ซื้อด่วน · Quick buy</div>
            <h2>{draft ? 'ทำร่างต่อ' : 'ซื้อด่วนใบใหม่'}</h2>
          </div>
          <button className="xbtn" onClick={onClose} aria-label="ปิด">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6" /></svg>
          </button>
        </div>

        <div className="drawer-body">
          <div className="prod-search" style={{ marginTop: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="#0A0A0A" strokeWidth="1.6" /><path d="M16.5 16.5L21 21" stroke="#0A0A0A" strokeWidth="1.6" /></svg>
            <input ref={searchRef} autoFocus value={q} placeholder="ยิงบาร์โค้ด / พิมพ์ SKU / ชื่อสินค้า"
                   onChange={e => { setScanNote(''); search(e.target.value); }} onKeyDown={onSearchKey} />
            {searching && <span className="sd-empty" style={{ margin: 0, whiteSpace: 'nowrap' }}>ค้น…</span>}
          </div>
          {scanNote && !q && <p className="qb-scan-note">{scanNote} — ยิงตัวถัดไปได้เลย</p>}
          {rows?.length === 0 && !searching && <p className="sd-empty">ไม่พบใน PD ตรงกับ “{q}”</p>}
          {rows?.length > 0 && (
            <div className="qb-results" style={{ marginBottom: 16 }}>
              {rows.map(p => {
                const inCart = cart.find(x => x.sku === p.sku);
                return (
                  <div className="qb-res" key={p.sku} onClick={() => addToCart(p)}>
                    {p.image_url ? <img src={IMG(p.image_url)} alt="" onError={e => { e.target.style.opacity = .15; }} /> : <div className="qb-noimg" />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="qb-name">{p.name}</div>
                      <div className="qb-sub mono">{p.sku}{p.on_hand != null ? ` · คงเหลือ ${p.on_hand}` : ''}</div>
                    </div>
                    <div className="qb-add">{inCart ? `✓ ×${inCart.qty}` : '+ เพิ่ม'}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="qb-ehead"><span>สินค้า</span><span className="num">จำนวน</span><span className="num">ต้นทุน/ชิ้น ฿</span><span className="num">รวม</span><span /></div>
          {cart.length === 0
            ? <p className="sd-empty" style={{ padding: '18px 0' }}>ยังไม่มีรายการ — ค้นด้านบนเพื่อเพิ่ม</p>
            : cart.map(l => (
              <div className="qb-eline" key={l.sku}>
                <div style={{ minWidth: 0 }}><div className="qb-name">{l.name}</div><div className="qb-sub mono">{l.sku}</div></div>
                <input className="under mono qb-in" inputMode="numeric" value={l.qty}
                       onChange={e => setLine(l.sku, { qty: e.target.value.replace(/[^0-9.]/g, '') })} />
                <input className="under mono qb-in" inputMode="decimal" value={l.unit_cost}
                       placeholder={l.purchase_cost != null ? String(l.purchase_cost) : '0'}
                       onChange={e => setLine(l.sku, { unit_cost: e.target.value.replace(/[^0-9.]/g, '') })} />
                <div className="qb-lt mono">฿{fmt((Number(l.qty) || 0) * (parseFloat(l.unit_cost) || 0), 2)}</div>
                <button className="qb-rm" onClick={() => setCart(c => c.filter(x => x.sku !== l.sku))} aria-label="ลบ">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" /></svg>
                </button>
              </div>
            ))}
          {cart.length > 0 && <div className="qb-total"><span>รวม {cart.length} รายการ · {fmt(totalQty)} ชิ้น</span><b className="mono">฿{fmt(totalThb, 2)}</b></div>}

          {unknown.length > 0 && (
            <div className="qb-unknown">
              <h5>ยังไม่รู้จัก ({unknown.length}) · ยิงมาจากมือถือ</h5>
              {unknown.map(u => (
                <div className="qb-urow" key={u.uid}>
                  {u.photo_url ? <img className="qb-uth" src={u.photo_url} alt="" onError={e => { e.target.style.opacity = .15; }} /> : <div className="qb-uth empty" />}
                  <div style={{ minWidth: 0 }}>
                    <div className="qb-name mono">{u.barcode}</div>
                    <div className="qb-sub">{[u.code && `รหัส ${u.code}`, u.price && `ราคาขาย ฿${u.price}`, u.note].filter(Boolean).join(' · ') || 'ไม่มีข้อมูลเพิ่ม'}</div>
                  </div>
                  <span className="num mono">{u.qty}</span>
                  {/* ปุ่มอยู่ในกล่องเดียว = กริดคงที่ 5 ช่อง (เพิ่มปุ่มแล้วไม่ล้น/ตกบรรทัดอีก) */}
                  <div className="qb-uact">
                    <button className="ghost-btn" onClick={() => setMatchUnknown(u)}>🔗 จับคู่สินค้าเดิม</button>
                    <button className="ghost-btn" onClick={() => setMakeProduct(u)}>สร้างสินค้า</button>
                  </div>
                  <button className="qb-rm" onClick={() => setUnknown(l => l.filter(x => x.uid !== u.uid))} aria-label="ลบ">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="fx-row" style={{ marginTop: 22 }}>
            <div className="field">
              <div className="lbl"><span className="th">ซัพพลายเออร์ / ที่มา</span><span className="opt">พิมพ์เพื่อค้น / เพิ่มใหม่</span></div>
              <input className="under" value={supplier} list="qbn-sup" onChange={e => setSupplier(e.target.value)} placeholder="เลือกจากรายชื่อ หรือพิมพ์ชื่อใหม่" />
              <datalist id="qbn-sup">{supList.map(x => <option key={x.id} value={x.company_name} />)}</datalist>
              {supplier.trim() && !supExists() && <div className="sd-empty" style={{ margin: '5px 0 0', color: 'var(--accent)' }}>+ จะเพิ่ม “{supplier.trim()}” เข้ารายชื่อซัพพลายเออร์อัตโนมัติ</div>}
            </div>
            <div className="field">
              <div className="lbl"><span className="th">วันที่ของจะถึงคลัง</span><span className="opt">ไม่บังคับ</span></div>
              <input className="under mono" type="date" value={eta} onChange={e => setEta(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <div className="lbl"><span className="th">หมายเหตุ</span><span className="opt">ไม่บังคับ</span></div>
            <input className="under" value={note} onChange={e => setNote(e.target.value)} placeholder="เช่น เลขบิล / รอบซื้อ" />
          </div>

          <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
            <span>กด “สร้างใบซื้อ” แล้วของจะขึ้นเป็น <b>สินค้ากำลังเข้า</b> ใน PD ทันที (ยังไม่เพิ่มสต๊อกจริง) · ตอนของมาถึงค่อยเปิดใบแล้วกด <b>รับของเข้าคลัง</b></span>
          </div>
          {msg && <p className="saved-note">{msg}</p>}
          <p className="err-note">{err}</p>
        </div>

        <div className="drawer-foot">
          <button className="ghost-btn" onClick={saveDraft} disabled={busy || (!cart.length && !unknown.length)}>
            บันทึกร่าง{draftId ? ' (อัปเดต)' : ''}
          </button>
          <button className="add-sup" style={{ padding: '12px 20px', fontSize: 13 }} onClick={createBuy} disabled={busy || !cart.length}>
            {busy ? 'กำลังสร้าง…' : 'สร้างใบซื้อ (กำลังเข้า)'}
          </button>
        </div>
      </aside>

      {matchUnknown && (
        <ProductPicker
          initialQuery={matchUnknown.code || ''}
          onClose={() => setMatchUnknown(null)}
          onPick={p => {
            const u = matchUnknown;
            // ของมีใน PD อยู่แล้วแค่ยังไม่มีบาร์โค้ดนี้ — เติมบาร์โค้ด (+ราคาขายจากป้ายถ้า PD ยังว่าง) กลับเข้า PD
            backfillBarcode(p.sku, p.name, u.barcode);
            setCart(c => c.some(x => x.sku === p.sku)
              ? c.map(x => x.sku === p.sku ? { ...x, qty: (Number(x.qty) || 0) + (Number(u.qty) || 1) } : x)
              : [...c, {
                  sku: p.sku, name: p.name, barcode: u.barcode, image_url: p.image_url ?? null,
                  purchase_cost: null, on_hand: null,
                  qty: Number(u.qty) || 1,
                  unit_cost: '',   // เติมด้วยราคาซื้อล่าสุดจาก PD ด้านล่าง (ทันทีที่ /api/pd/costs ตอบ)
                }]);
            setUnknown(l => l.filter(x => x.uid !== u.uid));
            setMatchUnknown(null);
            fillCostFromPd(p.sku, u.price);   // ต้นทุน = ราคาซื้อล่าสุด (บาท) · ราคาป้ายส่งเข้า PD ให้ ไม่เอามาเป็นทุน
          }}
        />
      )}

      {makeProduct && (
        <ProductDrawer
          line={{ code: makeProduct.code || '', desc: '' }}
          initialBarcode={makeProduct.barcode}
          initialPrice={makeProduct.price}
          initialPhotoUrl={makeProduct.photo_url}
          onClose={() => setMakeProduct(null)}
          onSaved={p => {
            setCart(c => (c.some(x => x.sku === p.sku) ? c : [...c, {
              sku: p.sku, name: p.name, barcode: makeProduct.barcode, image_url: p.image_url ?? null,
              purchase_cost: p.purchase_cost ?? null, on_hand: null,
              qty: Number(makeProduct.qty) || 1,
              unit_cost: p.purchase_cost != null ? String(p.purchase_cost) : '',
            }]));
            setUnknown(l => l.filter(x => x.uid !== makeProduct.uid));
            setMakeProduct(null);
          }}
        />
      )}
    </>
  );
}
